// =============================================================================
// Spheromak TypeScript Client
// Auto-generated - Do not edit manually
// API Version: 0.1.0
// Generated at: runtime
// =============================================================================

import { encode, decode } from '@msgpack/msgpack';

// =============================================================================
// Protocol Constants
// =============================================================================

const PROTOCOL_VERSION = 1;

const MessageType = {
  Request: 0x01,
  Response: 0x02,
  StreamStart: 0x03,
  StreamChunk: 0x04,
  StreamEnd: 0x05,
  AuthChallenge: 0x06,
  AuthResponse: 0x07,
  Heartbeat: 0x08,
  Error: 0x09,
  StreamError: 0x0A,
  Push: 0x0B,
  SessionResume: 0x0E,
  StreamResume: 0x0F,
  // Server → client, unsolicited: the carrier already authenticated this
  // connection. Only a server with an upgrade gate ever sends it.
  AuthEstablished: 0x10,
} as const;

const MessageFlags = {
  AUTHENTICATED: 0x0001,
  COMPRESSED: 0x0002,
  // 0x0004 reserved (formerly ENCRYPTED) — confidentiality is wss:// + the PQ lock.
  STREAM: 0x0008,
  BINARY_PAYLOAD: 0x0010,
  RESUMABLE: 0x0020,
} as const;

/** The server's `MFA_REQUIRED` protocol code: a first factor passed but a
 * second one is outstanding. Not a failure — see `AuthOutcome`. */
const MFA_REQUIRED_CODE = 1012;

/**
 * A fresh message id.
 *
 * ⚠️ **Not `crypto.randomUUID()` directly, and this is a correctness fix rather
 * than a style one.** `randomUUID` is restricted to *secure contexts* — HTTPS,
 * or `localhost`. A page served over plain HTTP from a LAN address is not one,
 * so `crypto.randomUUID` there is `undefined` and the first request a client
 * ever sends throws `crypto.randomUUID is not a function`.
 *
 * That is not an exotic deployment: it is every application served to other
 * machines on a local network without a certificate, and it is invisible during
 * development because `localhost` *is* a secure context. Measured in an Android
 * WebView on `http://10.0.2.2:8080` — Chrome 143, `isSecureContext: false`,
 * `crypto.subtle` and `crypto.randomUUID` both `undefined`, and
 * `crypto.getRandomValues` present.
 *
 * `getRandomValues` is available in insecure contexts, so the fallback is a
 * real RFC 4122 version-4 UUID from the same CSPRNG rather than a weaker id.
 * `Math.random()` is deliberately **not** a fallback: a message id collision is
 * a response delivered to the wrong caller.
 */
function spheromakMessageId(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();

  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  // Version 4, variant 10xx — the two fields RFC 4122 fixes.
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;

  const hex: string[] = [];
  for (let i = 0; i < 16; i++) hex.push((bytes[i] ?? 0).toString(16).padStart(2, '0'));
  return (
    hex.slice(0, 4).join('') + '-' +
    hex.slice(4, 6).join('') + '-' +
    hex.slice(6, 8).join('') + '-' +
    hex.slice(8, 10).join('') + '-' +
    hex.slice(10, 16).join('')
  );
}

// =============================================================================
// Message Types
// =============================================================================

interface SpheromakMessage {
  version: number;
  messageType: number;
  flags: number;
  messageId: string;
  controllerHash: bigint;
  methodHash: bigint;
  payload: Uint8Array;
}

// =============================================================================
// Error Types
// =============================================================================

export interface SpheromakError {
  code: string;
  message: string;
  details?: Record<string, unknown>;
}

export class SpheromakApiError extends Error {
  // Declared and assigned rather than written as constructor parameter
  // properties: those are TypeScript-only syntax that has to be *transformed*
  // rather than erased, so they are rejected under `erasableSyntaxOnly` (and by
  // any type-stripping runtime, such as Node's).
  readonly code: string;
  readonly details: Record<string, unknown> | undefined;

  constructor(
    code: string,
    message: string,
    details?: Record<string, unknown>
  ) {
    super(message);
    this.code = code;
    this.details = details;
    this.name = 'SpheromakApiError';
  }

  static fromError(err: SpheromakError): SpheromakApiError {
    return new SpheromakApiError(err.code, err.message, err.details);
  }
}

/** Decode an `Error` frame's payload.
 *
 * The wire format is BINARY, not MessagePack (`ErrorPayload::to_bytes`):
 * a big-endian u32 code, the UTF-8 message, then an optional NUL separator
 * followed by UTF-8 details. Decoding it as MessagePack — as this client used
 * to — throws, and the error reaches the caller as an untyped `Error` carrying
 * the raw bytes as text, with the code lost.
 *
 * Returns `null` for a malformed payload (short, or not valid UTF-8 — invalid
 * sequences are rejected rather than replaced, so a payload cannot smuggle
 * replacement characters into a message).
 */
function decodeErrorPayload(payload: Uint8Array): SpheromakError | null {
  if (payload.length < 4) return null;
  const view = new DataView(payload.buffer, payload.byteOffset, payload.byteLength);
  const code = view.getUint32(0, false);
  const rest = payload.subarray(4);
  const separator = rest.indexOf(0);
  const decoder = new TextDecoder('utf-8', { fatal: true });
  try {
    if (separator === -1) {
      return { code: String(code), message: decoder.decode(rest) };
    }
    return {
      code: String(code),
      message: decoder.decode(rest.subarray(0, separator)),
      details: { text: decoder.decode(rest.subarray(separator + 1)) },
    };
  } catch {
    return null;
  }
}

// =============================================================================
// Authentication Types
// =============================================================================

/** The outcome of an authentication attempt.
 *
 * A first factor that passes against an MFA-covered identity does NOT mint a
 * session: the server replies `MFA_REQUIRED` and waits for the code. That is a
 * legitimate outcome rather than a thrown error, so it is reported here.
 */
export type AuthOutcome = 'authenticated' | 'mfaRequired';

export interface AuthCredentials {
  /** Client identifier. */
  clientId: string;
  /** Ed25519 private key seed (32 bytes; the leading 32 bytes are used). */
  privateKey: Uint8Array;
  /** If true, self-register this identity (the server must allow it — off by default). */
  isRegistration?: boolean;
  /**
   * Pin the server's Ed25519 auth public key (32 bytes). When set, a challenge
   * carrying any other key is rejected before signing. Without a pin, server
   * identity rests on the channel (wss / the lock).
   */
  serverPublicKey?: Uint8Array;
}

// =============================================================================
// Connection Types
// =============================================================================

export type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'authenticating' | 'authenticated' | 'reconnecting';

export type ConnectionEventType = 'connected' | 'disconnected' | 'reconnecting' | 'reconnected' | 'reconnectFailed' | 'authenticated' | 'mfaRequired' | 'stateChange';

export interface ConnectionOptions {
  /** Reconnect automatically on disconnect (default: true) */
  autoReconnect?: boolean;
  /** Maximum reconnect attempts (default: 5) */
  maxReconnectAttempts?: number;
  /** Reconnect delay in ms (default: 1000) */
  reconnectDelay?: number;
  /** Connection timeout in ms (default: 10000) */
  timeout?: number;
  /** WebSocket subprotocol(s) to request */
  subprotocols?: string[];
  /** Buffer requests while reconnecting (default: true) */
  bufferRequests?: boolean;
  /** Maximum buffered requests (default: 100) */
  maxBufferedRequests?: number;
  /** Buffer timeout in ms (default: 300000 = 5 minutes) */
  bufferTimeout?: number;
  /** Restore subscriptions on reconnect (default: true) */
  restoreSubscriptions?: boolean;
  /** Advanced: supply the wasm lock module yourself instead of dynamically importing it
   * from the server (e.g. a bundled build, or a non-browser runtime that cannot import an
   * HTTP URL). Must return a ready {@link LockFactory} (the wasm already instantiated). */
  lockLoader?: () => Promise<LockFactory>;
  /** The server's PQ-lock static public key (64 hex chars), supplied at runtime.
   *
   * This is the **strong** form of the pin: an out-of-band value an active attacker
   * cannot influence. It overrides both the key baked in at generation time and
   * discovery, and is what a client generated *offline* must use — the pin is
   * per-server, so an offline generator cannot know it even in principle. */
  lockServerPublicKey?: string;
  /** Ask the server whether it runs the lock (`GET /spheromak/lock.json`) when no pin is
   * known from {@link ConnectionOptions.lockServerPublicKey} or the generation-time
   * const (default: true).
   *
   * Trust-on-first-use: the answer comes from the origin it describes, so it is only as
   * good as the TLS to that origin. It exists because the alternative for a client with
   * no pin is worse — silently speaking plaintext to a server that requires the lock.
   * Set {@link ConnectionOptions.requireLock} to refuse a downgrade. */
  lockDiscovery?: boolean;
  /** Refuse to connect unless the lock is established (default: false).
   *
   * Turns a silent downgrade into a connect error. Set this when the deployment is known
   * to run the lock. */
  requireLock?: boolean;
}

/** A server push delivered to this connection (pub/sub fan-out, or a direct send).
 *
 * The wire frame carries the topic's **hash**, not its name, so match with
 * {@link SpheromakClient.isPushTopic} rather than comparing strings. */
export interface PushEvent {
  /** xxHash64 of the topic this was published to. */
  topicHash: bigint;
  /** The raw published payload. */
  payload: Uint8Array;
  /** Decode the payload with the client's configured codec. */
  decode: () => unknown;
}

interface BufferedRequest {
  messageData: ArrayBuffer;
  resolve: (value: unknown) => void;
  reject: (error: Error) => void;
  bufferedAt: number;
}

// =============================================================================
// Streaming Types
// =============================================================================

/** Iterator result for streaming responses */
export interface StreamChunkEvent<T> {
  messageId: string;
  data: T;
}

/** Async iterator for streaming responses */
export interface ResponseStream<T> extends AsyncIterable<T> {
  /** Stream ID */
  readonly streamId: string;
  /** Whether the stream is still active */
  readonly isActive: boolean;
  /** Cancel the stream */
  cancel(): void;
}

/** Handle for uploading data to the server.
 *
 * `TItem` is the per-chunk item type; `TResp` is the call's final response
 * (client-streaming calls only — bidirectional calls complete via their
 * download stream instead). */
export interface UploadStream<TItem = unknown, TResp = unknown> {
  /** Stream ID */
  readonly streamId: string;
  /** Total bytes sent */
  readonly bytesSent: number;
  /** Whether the stream has ended */
  readonly isEnded: boolean;
  /** Send a chunk of data */
  send(data: Uint8Array): Promise<void>;
  /** Send a serializable object */
  sendItem(item: TItem): Promise<void>;
  /** End the upload without awaiting the final response */
  end(): Promise<number>;
  /** End the upload and await the call's final typed response */
  finish(): Promise<TResp>;
  /** Cancel the upload */
  cancel(): void;
}

// =============================================================================
// Payload Types (generated from the server's registered payload schemas)
// =============================================================================

/** One genre, as the lobby's picker shows it — phase 16h. */
export interface GenreNodeView {
  /** What a party stores when it picks this. */
  id: string;
  /** Display name. */
  name: string;
  /** `null` for a family, set for a subgenre. **Grouping is the UI's job.** */
  parent_id?: string | null;
  /** Stable identifier. */
  slug: string;
}

/** Everything a lobby's selection picker may offer — phase 16g-2.  ⚠️ **Both lists are already narrowed to what this installation serves.** The picker renders what it is given; it does not know there is a scope, and it must not, because the scope is enforced in [`crate::catalog::selection`]'s own predicate either way. */
export interface SelectionOptions {
  /** The decades on offer, as the first year of each. */
  decades: number[];
  /** Families first, then subgenres, each alphabetical. */
  genres: GenreNodeView[];
}

/** Where to get a card's thirty seconds of audio.  Serialised **externally tagged** (`{"provider":"local","sha256":"…"}`) so that a payload names its provider in a field a reader can branch on without knowing the variant list — a client built before a third provider existed sees an unrecognised `provider` and can say so, rather than failing to parse. */
export type AudioRef = { provider: 'local'; sha256: string } | { provider: 'spotify'; track_id: string };

/** Start playing a clip.  An instruction rather than state, which is why it has its own topic: a client re-renders the board on every board message, and restarting the audio each time would make every round stutter. */
export interface ClipCue {
  /** Where to get the audio — phase 19b.  A local clip is a content address for `GET /api/clip/{sha256}` and says nothing about the song. A Spotify id does say something, which is why this payload goes to the clip topic and nowhere else — see [`crate::game::audio`]. */
  audio: AudioRef;
  /** How long to play for. */
  seconds: number;
}

/** An acknowledgement. The state itself arrives on the topics.  Distinct from the lobby's `Ack` rather than shared with it: the two controllers generate into one TypeScript namespace, and two payload types with one name is a collision waiting for whichever grows a field first. */
export interface GameAck {
  /** Always `true`. A refusal arrives as an error, carrying the engine's own reason. */
  ok: boolean;
}

/** What the shop charges, and how long the game is.  The prices live in `[game]` config (they are balance, discovered by playing), so a client cannot know them and must be told. Sent once with [`Watching`] rather than on every [`GameState`]: they cannot change during a game — the engine owns its rules for the game's whole life — and repeating them sixty times a round would be pure noise.  **The shop shows every item, disabled with a reason rather than hidden** (phase 5's plan), which is why the client needs the price of a thing the player cannot currently afford. */
export interface GameRules {
  /** Hide your timeline from challengers for one round. */
  blind_timeline: number;
  /** Cards on a completed timeline — what the progress bar counts towards. */
  cards_to_win: number;
  /** Contest another side's placement. */
  challenge: number;
  /** Deathmatch: a wrong placement this round costs no card. */
  insurance: number;
  /** Remove one wrong option from each of your grids. */
  narrow_options: number;
  /** Discard the card, redeal, keep the turn. */
  skip: number;
  /** Take a placed card off another side. Disabled in Team mode. */
  steal: number;
}

/** The answer, once it is allowed out. */
export interface AnswerView {
  /** Who it was by. */
  artist: string;
  /** Which artist option was right. */
  artist_option: number;
  /** What it was called. */
  title: string;
  /** Which title option was right. */
  title_option: number;
  /** **The year.** Present only from `Reveal`, and `None` on an **undated round** (phase 19g) even then.  ⚠️ Two different absences meet in this one field, and a screen must not confuse them: the whole [`AnswerView`] is absent before `Reveal` (critical rule 7), while *this* being `None` inside a present answer means the card never had a year at all. The first is "not yet", the second is "never". */
  year?: number | null;
}

/** A gap a challenger has spoken for, and who spoke for it.  One claim per slot is the contention rule, so this list is what makes challenge aimable at all: a challenger who cannot see which gaps are gone is aiming blind and will simply be refused.  📌 **The side is here as of 20d** — see [`RoundView::challenged_slots`] for the argument it overturns. A challenge is a move made out loud in a room; the screen was being vaguer than the party. */
export interface ChallengedSlotView {
  /** Who claimed it. */
  side: SideId;
  /** The gap they claim, indexed into the **active side's** timeline. */
  slot: number;
}

/** What one side answered — the options they picked, not the right ones.  Two indices into [`RoundView::artist_options`](crate::game::RoundView) and `title_options`, which are already on the wire. Rendering it is a lookup rather than a second copy of the grids, the same way [`PlacementView`](crate::game::PlacementView) indexes a timeline it does not duplicate. */
export interface GuessView {
  /** The artist option they chose, if they chose one. `None` is a real answer — somebody who never picked — and reads on screen as "no answer". */
  artist_option?: number | null;
  /** The title option they chose, likewise. */
  title_option?: number | null;
}

/** Which game is being played.  The rules behind each live in [`crate::game`] as separate strategies over one state machine. This enum is only the choice a lobby makes.  `Serialize`/`Deserialize` because [`crate::game::Game`] carries one and phase 5 snapshots the whole game to resume a party after a restart. The serde representation is `snake_case`, which is deliberately the same string [`Mode::as_str`] stores in Postgres — a test below holds the two together, so a column and a snapshot cannot come to disagree about what "deathmatch" looks like. */
export type Mode = 'classic' | 'deathmatch' | 'team';

/** Something the room should be told about. */
export type Notice = { item: ShopItem; kind: 'bought'; side: SideId } | { from: SideId; kind: 'stole'; side: SideId; track: TrackId } | { from: SideId; kind: 'stealing'; side: SideId; track: TrackId } | { from: SideId; kind: 'defended'; side: SideId } | { kind: 'revoted'; side: SideId } | { kind: 'challenged'; side: SideId; slot: number } | { kind: 'passed'; side: SideId } | { kind: 'narrowed'; side: SideId } | { kind: 'party_stalled' } | { kind: 'stall_vote_opened' } | { kind: 'stall_vote_kept_waiting' } | { kind: 'party_resumed' };

/** One notice, with the sequence number a client tracks.  The sequence is what lets a phone toast only what it has not seen: it keeps the highest `seq` it has rendered and toasts anything above it. That works across a reconnect, which a diff of two snapshots does not. */
export interface NoticeEntry {
  /** What happened.  ⚠️ **A nested field, not `#[serde(flatten)]`.** Flattening reads better on the wire and produces a `JsonSchema` the client generator cannot express: `NoticeEntry` came out of `sonolume-codegen` as `{ seq: number }` with the entire payload silently missing. The runtime JSON would still have carried it, which makes it worse than a compile error — the generated client is the source of truth for wire shapes, and a shape that quietly forgets half of itself is exactly the drift it exists to prevent. */
  notice: Notice;
  /** Monotonic within a turn, from 1. */
  seq: number;
}

/** How one side did this round.  Every field is a separate award because scoring is split — knowing the artist but not the song still pays, which is the main thing keeping a weaker player in the game. */
export interface Outcome {
  /** Named the artist. */
  artist_correct: boolean;
  /** What the three awards and the bonus add up to. */
  coins_awarded: number;
  /** Named the exact year **and** placed correctly. The bonus is not paid for a lucky year on a wrong slot: the placement is the answer, the year is the flourish. */
  exact_year_correct: boolean;
  /** Which side. */
  side: SideId;
  /** Put the card in a chronologically correct slot. */
  slot_correct: boolean;
  /** Named the title. */
  title_correct: boolean;
  /** Whether this side keeps the card.  Follows `slot_correct` today. It is a separate field because a challenge (4d) can move the card to a side that never placed it. */
  wins_card: boolean;
}

/** A steal that has been paid for and is waiting to be answered. */
export interface PendingStealView {
  /** Who is taking it. */
  by: SideId;
  /** What blocking costs, so the victim's screen can say so without a second copy of the price table. */
  defence_price: number;
  /** Whose card, and the only side that may block it. */
  from: SideId;
  /** Which card — resolve it against that side's timeline rather than duplicating it here, the way `Notice::Stole` already does. */
  track: TrackId;
}

/** Where a round is.  ```text TurnReady → Listening → Guessing → Placing → Challenge → Reveal → Dispute → Settled │ Void → redeal, replay ```  [`Phase::Settled`] is a real resting phase rather than a transient: the display needs a beat to animate coins landing, and ending that beat on a timer or an explicit continue is cheaper than making the display infer one.  A **void** is not a phase. It is a transition out of [`Phase::Dispute`] that discards the round and lands back at [`Phase::TurnReady`] with the same active side — see ADR 0009. */
export type Phase = 'turn_ready' | 'listening' | 'guessing' | 'placing' | 'challenge' | 'reveal' | 'dispute' | 'settled' | 'finished';

/** How long the phase in flight has left.  # Why this is not on [`Snapshot`]  The engine arms timers and never learns what time it is (ADR 0003), so it cannot answer this and must not be given the means to. The **runner** can: it is what turned [`Effect::StartTimer`](crate::game::Effect) into a `tokio::time::sleep`, so it knows both when the clock started and how long it was set for. This rides *beside* the snapshot rather than inside it, which keeps every field of the engine's single exit a fact the engine owns.  # Why it is a duration and not an instant  A deadline as a wall-clock timestamp is only as good as the difference between two clocks, and one of the clients here is **a television** — a packaged app on a box whose clock nobody has ever checked. A phone a minute fast would show every round already expired. Milliseconds-from-now is immune to that: the receiver adds it to its own `now`, and the only error is the flight time of one message.  Recomputed on every publication, so a phone that reconnects mid-phase is told what is *actually* left rather than what was left when the timer was armed. */
export interface PhaseClock {
  /** Milliseconds from now until the phase runs out. `0` once it has. */
  ends_in_ms: number;
  /** How long this phase gets in total — the denominator for a bar or a ring, which a client cannot otherwise know: the timer lengths are `[game]` config and only the shop's prices reach the wire. */
  total_seconds: number;
}

/** Where the active side says the card goes.  # This is a claim, not the answer — and phase 16a is why it exists  `RoundPanel` asked *"¿Alguien la impugna?"* without ever showing what *la* was: a non-active player was invited to contest a placement they had never been shown, in a ten-second window, for a coin. Challenge was not badly tuned, it was **unaimable**, and it had been since phase 4.  Publishing this does **not** touch Critical Rule 7. The slot is what the active side asserts; the year is what the engine knows. `view.rs` already documents that the years of *placed* cards are public — a placed card is face-up on the board — so a slot index plus a face-up timeline is exactly the information a human at the table already has, and nothing more.  # Render it against [`SideView::timeline`], do not duplicate it here  The timeline this indexes into is already on the wire, in the [`SideView`] whose `id` matches [`PlacementView::side`]. Carrying a copy of the neighbouring cards here would be a second definition of a shape that already exists, which is the drift the frontend rules forbid.  **The index cannot go stale.** Timelines only grow at settlement, and `steal` is sold at `TurnReady` and `Settled` only (`shop.rs`), so nothing can move a card between the placement and the reveal that judges it.  ⚠️ **A blind timeline cannot be judged, and that is the purchase working.** If the active side bought `blind_timeline`, its [`SideView::timeline`] is empty for everybody else and `timeline_hidden` is set. The slot is still published — a challenger may know *that* a claim was made — but there are no face-up years to reason about, so the client should say the board is hidden rather than draw a slot into nothing. */
export interface PlacementView {
  /** Whose claim it is: the active side, whose timeline the slot indexes. */
  side: SideId;
  /** The gap they chose, counted as [`crate::game::Timeline::slots`] does — `0` is before the oldest card, `n` is after the newest. */
  slot: number;
}

export type PlayerId = string;

/** Whether a round has a year to place, or only an artist and a title.  Derived from [`Card::year`] rather than stored, so it cannot disagree with the card it describes. Published on the wire so a screen can say what kind of round this is before `Reveal` — which leaks nothing: *that* there is no year is not a fact about what the year would have been. */
export type RoundKind = 'dated' | 'undated';

/** The round in flight. */
export interface RoundView {
  /** `Some` only from `Reveal` onward. This is the whole rule.  Skipped entirely rather than serialized as `null`, so a pre-reveal payload does not even carry the word — which lets the no-leak test assert the absence of a *key* rather than the presence of a particular value. */
  answer?: AnswerView | null;
  /** The artist grid. Contains the right answer, and says nothing about which one it is. */
  artist_options: string[];
  /** The side that took the card by challenging, once the answer is out.  # Why a client cannot work this out, and why it kept getting it wrong  **Phase 18, finding #13**: *"another player challenged and was right on the year, but it said falló and gave him the card anyway."* The engine was correct throughout — a winning challenger gets `wins_card` and is paid — and the *display* drew its headline verdict from [`Outcome::slot_correct`](crate::game::Outcome), which describes a placement the challenger never made and is therefore always `false` for them. The row read *"Falló … Ganó la carta"*: two true answers to two questions, neither of them the one the screen was asking.  ⚠️ **Not inferrable from `wins_card`.** 16j-2 recorded this when the same value was added to `rounds`: in a concurrent mode several sides win cards in one round, so "won a card and did not place well" is not the same statement as "won by challenging". The engine already knows which, in one place, and this publishes that rather than inviting the client to guess.  Gated with [`RoundView::outcomes`] — before `Reveal` there is nothing to have won. */
  challenge_won_by?: SideId | null;
  /** Slots on the active side's timeline that a challenger has already claimed — phase 16b.  **Public for the same reason [`RoundView::placement`] is, and necessary for the rule to be playable at all:** one claim per slot is the contention rule, so a challenger who cannot see which gaps are taken is aiming blind and will simply be refused. A claim is somebody's assertion, never the answer.  📌 **Who claimed a slot is here, and was deliberately not, until 20d.** The old argument was [`RoundView::flags`]': a count rather than a roster, because naming somebody turns a judgement into a thing to have an opinion about. The maintainer overturned it for challenges, and the two are not the same act: *"'Somebody already claimed that gap' should be 'Claimed by {name}'."*  A flag is a quiet report that the catalogue is wrong and anonymity is the point (ADR 0009). A challenge is a **move in a party game**, announced out loud across a room, paid for, and scored — arguing with it is the mechanic, not a side effect. Withholding the name made the screen vaguer than the room already was.  ⚠️ Still a claim, never the answer: a name and a slot say who asserted what, and nothing about which of them is right.  Empty before the challenge window, like `placement`. */
  challenged_slots: ChallengedSlotView[];
  /** How many people have said this year is wrong (ADR 0009).  A count rather than a roster: *who* raised a correction is nobody else's business, and naming them turns a bug report into a thing to have an opinion about — which is the failure ADR 0009's alternatives section rejected. Zero until somebody flags, and flags only happen from `Reveal`. */
  flags: number;
  /** How many more are needed before the round is thrown away.  `0` once the threshold is met. Shown so a corroborate prompt can say what pressing it does; **the prompt still asks whether the year is wrong**, never whether to void the round. */
  flags_needed: number;
  /** Whether this round has a year at all — phase 19g.  ⚠️ **Publishing this before `Reveal` leaks nothing.** *That* there is no year is not a fact about what the year would have been, and a screen has to know it from the first second: an undated round never offers a slot, and a stepper that discovered this at `Reveal` would have drawn a placement UI nobody can use. */
  kind: RoundKind;
  /** What each side earned, once the answer is out.  Empty before `Reveal` for the same reason [`RoundView::answer`] is absent: "you got the year right" is the year. */
  outcomes: Outcome[];
  /** What the active side claims, from the challenge window onward.  `Some` exactly when [`Phase::placement_is_public`] and the active side has actually committed a slot. Skipped rather than serialized as `null`, for the same reason [`RoundView::answer`] is: it lets a test assert the absence of a *key*.  ⚠️ **This is deliberately not gated on `answer_is_public`.** See [`PlacementView`] — the whole of phase 16a is the one phase of daylight between the two predicates. */
  placement?: PlacementView | null;
  /** Which round. */
  round_no: number;
  /** The title grid, likewise. */
  title_options: string[];
}

/** A purchasable power.  The enum is the authority on what exists; [`Shop`] only says what each costs. Every purchase goes through one code path so coin accounting has exactly one place to be wrong and one place to be tested. */
export type ShopItem = 'challenge' | 'steal' | 'skip' | 'narrow_options' | 'blind_timeline' | 'insurance';

export type SideId = string;

/** One side, as everybody sees it. */
export interface SideView {
  /** Whether nobody is at this side's controls.  True only when **every** member has gone quiet — a team with somebody still playing is not absent. Reversible: it clears the moment one of them comes back, with their timeline intact.  On the wire beside `eliminated` rather than folded into it, because the two need different words on screen and the difference is the whole point (see [`crate::game::presence`]). Both mean "not playing this round". */
  absent: boolean;
  /** How many cards they hold.  Accurate even when the timeline is hidden: how close somebody is to winning is public, and a board that could not show it would be unreadable. `blind_timeline` hides *which* cards, not *how many*. */
  card_count: number;
  /** Settled coins. What this round owes is not shown until it settles, because until then it is not real (ADR 0009). */
  coins: number;
  /** Whether they are out **by the rules** — a Deathmatch knockout.  Earned and permanent. Distinct from [`SideView::absent`], which is circumstantial and reversible: a screen that showed "eliminated" over somebody whose phone died would be telling the room something untrue about a game they are watching. */
  eliminated: boolean;
  /** What this side answered, from the moment everybody has answered.  **Phase 18, finding #1.** A claim, not the answer: it says which option somebody picked, never which option is right. Gated on [`Phase::guess_is_public`](crate::game::Phase::guess_is_public) — 16a's argument applied to the guess, which is the half it stopped short of.  ⚠️ **One phase earlier than the slot** (20d). The two used to share `placement_is_public` and they should not: a placement is only committed by `Challenge`, but a *guess* is committed by `Placing`, and the room spent that whole phase watching somebody place a card with no idea what they had said it was.  Skipped rather than serialized as `null` before the window opens, for the reason [`RoundView::answer`](crate::game::RoundView) is: it lets a test assert the absence of a *key*.  📌 In Classic only the active side plays, so this is one side's claim. In Deathmatch and Team every side plays every round and every claim becomes visible at once — which is safe, because by `Placing` the grids have closed and nobody can copy anybody. */
  guess?: GuessView | null;
  /** Whether they have placed this round — **not** where. */
  has_placed: boolean;
  /** Which side. */
  id: SideId;
  /** Who plays for it. */
  members: PlayerId[];
  /** Their cards, oldest first. Empty when [`SideView::timeline_hidden`]. */
  timeline: TimelineCardView[];
  /** Whether `blind_timeline` is hiding this side's cards from you. */
  timeline_hidden: boolean;
}

/** Everything a client is told. */
export interface Snapshot {
  /** Whose placement is on the table. */
  active_side: SideId;
  /** Which game. */
  mode: Mode;
  /** What the room has been told this turn, oldest first — phase 16d.  On the [`Snapshot`] rather than the round, because `steal` happens at `TurnReady` and `Settled`, where there is often no round at all — which is exactly the moment a steal needed announcing and had nowhere to live.  A client toasts the entries whose `seq` it has not already rendered, and may draw the whole list as a feed. One stream, two renderers.  ⚠️ **Carries nothing about the round in flight.** Every notice is either about a *placed* card, whose year is public because it is face-up, or about an action somebody took. See [`crate::game::notice`] — notably that a narrow says *who*, never *which options*. */
  notices: NoticeEntry[];
  /** A steal waiting on the side it is aimed at — phase 18, finding #24.  The victim's phone offers the block from this; everybody else's says what the room is waiting for, which is the difference between a pause and a frozen screen.  **Not an answer leak.** A pending steal names a *placed* card, whose year is already face-up on the timeline it is being taken from — the same argument [`SideView::timeline`] makes. Skipped rather than serialized as `null` so a test can assert the absence of a key. */
  pending_steal?: PendingStealView | null;
  /** Where the round is. */
  phase: Phase;
  /** Cards banked by tied Deathmatch rounds — phase 20h-4, ADR 0029.  🔴 **A count, and it has to be on screen.** A pot nobody can see is a mechanic nobody knows about: the room watches a round where everybody was right hand its card to no one, and unless something says where it went, that reads as the game losing a card rather than banking it.  ⚠️ **A count, never the cards.** They carry years, and a year on a client before `Reveal` is the one mistake that makes the game cheatable — these are cards nobody has been dealt yet as far as a player is concerned. */
  pot: number;
  /** The round in flight. */
  round?: RoundView | null;
  /** Which round, from 1. */
  round_no: number;
  /** Every side, in turn order. */
  sides: SideView[];
  /** A party nobody can move on, and the vote about it — phase 21, ADR 0030.  🔴 **The screen this phase exists for.** A stalled party looks exactly like a working one that nobody is touching — the board is fine, the timelines are fine, and the reason nothing is happening is invisible. Without this the notice is the only trace, and a phone that reconnects mid-stall would show a live game that never advances.  **No answer leak.** It carries who has voted and what they said, and nothing about the round.  Skipped rather than serialized as `null`, like `pending_steal`, so a test can assert the absence of a key. */
  stall?: StallView | null;
  /** Voids spent, against `max_voids_per_game`. */
  voids_used: number;
  /** Who won. */
  winner?: SideId | null;
  /** Your own private slice. `None` for a display or spectator. */
  you?: YouView | null;
}

/** A party waiting on somebody who can move it on.  ⚠️ **`voting` is what a client switches on, not `votes.is_empty()`.** The grace period and a vote nobody has answered yet look identical from the tally, and only one of them has buttons on it. */
export interface StallView {
  /** How many answers either way settle it, out of the players present. */
  needed: number;
  /** Who is still here, and therefore whose answer the vote is counted against.  📌 **Sent because the bar moves.** A majority is of the players present *now*, so a client that computed it from the roster would show "1 of 3" for a question that only needs one more answer — and the difference is exactly the thing this phase is about not getting wrong. */
  present: PlayerId[];
  /** Who has answered, and what they said. `true` is *close the party*.  Empty during the grace period. */
  votes: StallVoteView[];
  /** Whether the room is being asked yet, or still inside the grace period. */
  voting: boolean;
}

/** One player's answer. */
export interface StallVoteView {
  /** `true` closes the party; `false` keeps waiting. */
  close: boolean;
  /** Who answered. */
  player: PlayerId;
}

/** One member's vote, for their own side's eyes — phase 20h-3. */
export interface TeamVote {
  /** Who voted. */
  player: PlayerId;
  /** The gap they chose, or `None` if they have not chosen yet. */
  slot?: number | null;
}

/** A card on somebody's timeline.  These years **are** public: a placed card is face-up on the board, and the game is played by reading them. Only the round in flight is secret. */
export interface TimelineCardView {
  /** Who it is by. */
  artist: string;
  /** What it is called. */
  title: string;
  /** Which track, so a steal or a flag can name it. */
  track: TrackId;
  /** When it came out. */
  year: number;
}

export type TrackId = string;

/** Why a power cannot be bought right now.  # A bool was not enough, and three findings said so  [`Game::item_available`](crate::game::Game::item_available) answered `bool`, so every gate in the game arrived on a phone as the same greyed row reading *"not right now"*. The third real party produced three separate complaints that are all this one shape: powers that were unbuyable during `Listening` (finding #21), two new steal gates whose whole purpose is to be **understood** as rules (finding #22), and 16d's own note that `Shop.tsx` derives its own disabled sentences and therefore cannot know about a gate added since.  A rule the player cannot see is not a rule, it is a bug they report. So the engine names the refusal and the client owns the words — the same split [`Rejection::code`](crate::game::Rejection::code) already makes, and for the same reason.  # These are API  A client matches on them to pick a sentence, so renaming one drops a screen back to its fallback text. */
export type Unavailable = 'game_over' | 'wrong_phase' | 'not_in_this_mode' | 'not_your_turn' | 'spent_this_turn' | 'already_held' | 'already_challenged' | 'no_free_slots' | 'no_targets' | 'no_steals_left' | 'would_win_the_game' | 'steal_pending';

/** One power that is not on offer, and why. */
export interface UnavailablePurchase {
  /** Which power. */
  item: ShopItem;
  /** The rule that is in the way. Never affordability — that is arithmetic the client already has both halves of. */
  reason: Unavailable;
}

/** What one player may see about themselves.  Never present on a display's snapshot. */
export interface YouView {
  /** The artist option you chose. */
  artist_option?: number | null;
  /** What your side may buy **right now**, ignoring whether you can afford it.  Computed by [`Game::item_available`], the same predicate the purchase itself is checked against, so the shop cannot disagree with the engine about what is buyable. The alternative was mirroring the phase-and-mode table on the client, which is a second copy of a rule that changes.  **Not an answer leak:** availability turns on phase, mode and whose turn it is — never on the card. Price is on `Watching`, and "can you afford it" is arithmetic the client already has both halves of, which is why this list ignores cost: an item you cannot afford is disabled *for a different reason*, and a shop that conflated the two would teach nobody what exists.  📌 Kept beside [`YouView::unavailable_purchases`], which says *why* about everything absent from this list. Two views of one computation, because a client that only wants "can I press it" should not have to interpret a reason. */
  available_purchases: ShopItem[];
  /** Whether you have already claimed or passed in the challenge window.  Phase 16b. What the phone reads to stop offering a decision you have already made — the engine refuses a repeat anyway, so this is about not asking twice rather than about enforcement. */
  challenge_answered: boolean;
  /** Your side's settled coins. */
  coins: number;
  /** The exact year you named, once the answer is public.  **Withheld before `Reveal` even though it is your own input**, which looks over-cautious until you try to write the leak test without it: a legitimate echo of a player's guess is indistinguishable, by inspection of the payload, from the engine leaking the real year. Withholding it keeps `tests/game_no_leak.rs` an absolute rule with no exceptions, and ADR 0011 already settled that an absolute rule is worth more than a feature.  The cost is real but small: a phone that reconnects mid-round can say *that* you guessed a year (the boolean above) but not show it back until the reveal. The client knew what it typed; only a fresh client does not. */
  exact_year?: number | null;
  /** Whether you named an exact year — never *which* year, before `Reveal`.  See [`YouView::exact_year`] for why this boolean exists. */
  exact_year_submitted: boolean;
  /** Whether you have locked your guess — phase 18, finding #6.  A lock is final now, so the grids have to stop inviting an answer the engine will refuse with [`Rejection::AlreadyLocked`](crate::game::Rejection). Without this the rule could only be discovered by being refused, which is the complaint the whole of #21 is about. */
  guess_locked: boolean;
  /** Whether **you** have already said this year is wrong.  What the flag button reads from, and what makes the corroborate prompt appear for everybody else and not for the person who raised it. The engine refuses a second flag from the same player at no cost (`Rejection::AlreadyFlagged`); this is so the phone does not offer it. */
  has_flagged: boolean;
  /** Whether **you** may move the game on right now (phase 14 C3).  The engine's own answer, on the same terms as [`YouView::available_purchases`]: `start_round` used to take no player at all, and the only thing stopping anybody dealing was a phone choosing not to draw the button. Now the phone asks who may, and the engine refuses the move again regardless. */
  may_advance: boolean;
  /** Who you are. */
  player: PlayerId;
  /** Artist options `narrow_options` struck out for your side. */
  removed_artist_options: number[];
  /** Title options likewise. */
  removed_title_options: number[];
  /** What one costs, so the button can say it before it is pressed. */
  revote_price: number;
  /** Fresh votes your side may still buy this round.  Published for the reason [`YouView::steals_used`] is: a cap a player can only discover by being refused is a rule nobody knows. `0` with [`YouView::team_deadlocked`] set is the moment the card is lost. */
  revotes_left: number;
  /** Which side you play for. */
  side: SideId;
  /** The slot you placed in. */
  slot?: number | null;
  /** Steals your side gets for the whole game (finding #18). */
  steals_allowed: number;
  /** Steals your side has spent, of [`YouView::steals_allowed`].  Published for the same reason `voids_used` is: a cap a player can only discover by being refused is a rule nobody knows. The shop says *"2 of 3 left"* from these two numbers. */
  steals_used: number;
  /** Whether your side voted and nothing won — phase 20h-3, ADR 0029.  🔴 **The screen has to say this, or the round looks stuck.** A deadlocked side has no committed placement, so the placing window simply does not close and everybody watches a clock run down with no idea why. Team mode under `consensus` only; `false` everywhere else. */
  team_deadlocked: boolean;
  /** What each member of your side voted for, while the vote is open.  🔴 **Your own side only, and only in Team mode.** It is the shape of the tie — *"two of us said before, two said after"* — and without it a deadlock is a refusal with no explanation. ⚠️ It says nothing about the **answer**: these are claims, and which one is right is [`Phase::answer_is_public`](crate::game::Phase)'s business, untouched. */
  team_votes: TeamVote[];
  /** The title option you chose. */
  title_option?: number | null;
  /** Why each of the other powers is not on offer.  **Phase 18.** Three of the third real party's findings are the same shape: a rule the player could not see. `narrow_options` refused during `Listening` (#21) and both new steal gates (#22) reached the phone as one greyed row reading *"not right now"*, which is what a broken button looks like — and 16d had already noted that `Shop.tsx` derives its own disabled sentences and so cannot know about a gate added since.  The engine names the refusal and the client owns the words, exactly as [`Rejection::code`](crate::game::Rejection::code) does for a refused move. See [`Unavailable`].  Every [`ShopItem`] appears in exactly one of the two lists. */
  unavailable_purchases: UnavailablePurchase[];
}

/** A live game's state, as one recipient may see it.  Published on three kinds of topic and never returned by a method — see [`super::controller::watch`] for why state arrives on a topic rather than in a reply. Which snapshot this holds depends entirely on which topic it came from: the board topic carries the display's view, a player topic carries that player's. */
export interface GameState {
  /** Whether a game is running. `false` with no state means the party went back to the lobby. */
  live: boolean;
  /** How long the phase in flight has left, when anything is timing it.  **Beside the snapshot rather than in it**: the engine arms timers and never learns what time it is (ADR 0003), so a deadline is the runner's fact and not the engine's — see [`PhaseClock`]. Every phase the engine times has had one since phase 4 and **none of them reached the wire** until phase 14, so no screen could count anything down; a party played on 2026-08-17 found that out the way everyone else would have. */
  phase_clock?: PhaseClock | null;
  /** The state, for this audience. */
  state?: Snapshot | null;
}

/** Who a side **is**, in words somebody can say out loud.  [`crate::game::view::SideView`] carries `members: Vec<PlayerId>` and nothing else, because the engine plays sides and is deliberately indifferent to what one means (ADR 0003). That indifference has to be spent somewhere, and this is the second place it is spent — [`super::roster::roster_to_sides`] is the first.  **In couch mode the room answers "whose timeline is that"; over a call nothing does.** A display name is party data: it belongs to a profile or a team, it can change while a game runs, and routing it through the engine would put a `String` in the one module that must not grow dependencies.  So it rides here, with the rules and the layout, keyed by the id [`super::roster::roster_to_labels`] derives **the same way** [`super::roster::roster_to_sides`] derives a side. The alternative — joining the lobby's names to `side.id` in the browser — means writing *"in Classic a side is a profile, in Team mode a side is a team"* in TypeScript, which is a second copy of a rule that changes. */
export interface SideLabel {
  /** Who plays for it, for a team panel. One name in Classic and Deathmatch, where it is the same string as `name` and a client should not repeat it. */
  members: string[];
  /** What to put above the timeline — a player's name, or a team's. */
  name: string;
  /** Which side. Matches `SideView::id`. */
  side: SideId;
}

/** What [`super::controller::watch`] hands back.  The topic **names**, because a push frame carries only a topic hash: a client matches by hashing the string it subscribed to, and a client that rebuilt those strings from a format it remembered would be a second definition of them. */
export interface Watching {
  /** Where the board is published. */
  board_topic: string;
  /** Where clip cues are published. */
  clip_topic: string;
  /** `couch` · `remote` — **who plays the audio**.  In couch mode the display is the sink and the phones stay quiet; in remote mode every client is its own. Party configuration rather than game state, so it rides here with the rules instead of on a snapshot: the engine does not know what a device layout is and must not learn.  A couch party with no display open is silence, and that is deliberate — the answer is the pre-game screen telling the host to open the board, not a phone quietly taking over and hiding a setup mistake until round one. */
  device_layout: string;
  /** Which game, once one is running — and therefore the recap's address.  `None` before a game starts, and for a client that connects after one finished: the id comes from the **live** row, so a recap link is something a phone that was there is holding, rather than something any party code can be turned into.  Needed because nothing else on the wire carries it. Every topic is keyed by *party*, and a party plays several games — so the phone that wants to offer "see how that went" at `Finished` has a party code and no way to name the game that just ended. */
  game_id?: string | null;
  /** Whether a game is running. The state itself arrives on the topics below. */
  live: boolean;
  /** Where this connection's private slice is published. `None` for a display, which has no private slice by construction (ADR 0005). */
  player_topic?: string | null;
  /** Who each side is — see [`SideLabel`].  Read once, at `watch`, which means somebody who joins the party *after* another phone connected is missing from that phone's copy. At a party that is not an edge case, it is every party — so a client re-`watch`es when the game goes live, which is both idempotent and the moment the party's configuration stops changing.  Built from the **present** roster, so a side whose player has left the party has no entry here at all. That is a fact worth rendering rather than papering over: over a call, "why is nobody answering for that timeline" is a real question. */
  roster: SideLabel[];
  /** What the shop charges and how long the game is — see [`GameRules`]. */
  rules: GameRules;
}

/** Contest the active side's placement, **with an answer of your own**.  Phase 20h-1, ADR 0029. The slot is the only part that wins the card; the grids and the year pay coins, and **only if the slot was right** — naming the artist while pointing at the wrong gap earns nothing. */
export interface ChallengeRequest {
  /** Index into the artist grid. Optional: a challenge is still a valid claim about the slot alone. */
  artist_option?: number | null;
  /** An exact year, for the bonus. Wrong costs nothing. */
  exact_year?: number | null;
  /** The slot you claim is right, on the **active side's** timeline. */
  slot: number;
  /** Index into the title grid. */
  title_option?: number | null;
}

/** Say the year on screen is wrong (ADR 0009).  Free, and available to everybody from `Reveal` onward. */
export interface FlagRequest {
  /** A year you believe is right. **A hint for the re-check, never a value to apply.** */
  suggested_year?: number | null;
}

/** Choose options. Either grid may be left alone. */
export interface GuessRequest {
  /** Index into the artist grid. */
  artist_option?: number | null;
  /** Index into the title grid. */
  title_option?: number | null;
}

/** Put the card on your timeline. */
export interface PlaceRequest {
  /** An exact year, for the bonus. Wrong costs nothing. */
  exact_year?: number | null;
  /** Which slot. See [`crate::game::Timeline`] for what a slot is. */
  slot: number;
}

/** Buy a power that needs no target. */
export interface PurchaseRequest {
  /** What to buy. The engine's own enum, so the generated client gets the real union rather than a string somebody has to spell right. */
  item: ShopItem;
}

/** What came of rating a card. */
export interface RateAck {
  /** The server's own words, in English.  ⚠️ **Not the string the app shows.** It was, and the effect was an English sentence appearing under a Spanish reveal — the client renders `rate.ack.*` off `recorded` instead. This stays because it is what a log, a non-browser client and `just smoke` read, and because the assertion that it never overclaims (a rating is not a removal) is worth keeping where the claim is actually made. */
  message: string;
  /** Whether it was recorded. `false` only when there is no card to rate.  **This is what the app renders from**, together with the `keep` it sent: three outcomes, all of which the client already knows, so the copy lives with the rest of the copy and is translated with it. */
  recorded: boolean;
}

/** Why a player voted a card off — phase 18, finding #7.  # The reason is what makes the vote safe to act on  The room asked for 👍 / 👎 / **why**, and the why is not decoration: two distinct parties voting `off` suspend a track from the deal for the whole installation ([`PARTIES_TO_SUSPEND`]). A room that dislikes a perfectly good song is not evidence of anything wrong with it, so renaming the buttons without separating the meanings would convert a catalog-quality signal into a taste signal **and leave the destructive trigger wired to it**.  So a reason is something a player can answer honestly, and a category is something the server can act on — see [`VoteReason::is_defect`].  ⚠️ **The authority is the trigger, not this enum.** `0022_vote_reasons.sql` filters on the same two codes in SQL, for 1b's reason: the invariant must hold whichever path writes. A test holds the two in lockstep. */
export type VoteReason = 'wrong_year' | 'bad_clip' | 'unknown_song' | 'disliked';

/** Say what you thought of the card itself (phase 13).  A **different question** from [`FlagRequest`], and the difference is the reason both exist: a flag says *the year on screen is wrong* and voids a round; this says *that was a bad card* — the audio is not the song, the clip is broken, nobody in the room could have known it — and changes nothing about the game at all.  **It names no track**, for the reason no intent names a player: the card in flight is something the server knows and a payload should not get to choose. */
export interface RateRequest {
  /** `true` for a good card, `false` for one that is off.  A boolean rather than a string, so a client cannot invent a third verdict and a fourth one is a wire change rather than a silent typo. */
  keep: boolean;
  /** What is wrong with it, in their words. Shown to a curator, never parsed, and never a year. */
  reason?: string | null;
  /** **Which kind of wrong** — phase 18, finding #7.  ⚠️ **This is the field the destructive half turns on.** Two distinct parties voting `keep: false` used to suspend a track from the deal for the whole installation on the strength of the boolean alone — so a room that simply did not enjoy a well-made song could take it out of the game everywhere. Only a *defect* reason counts now ([`VoteReason::is_defect`](crate::catalog::curation::VoteReason)), and a bare dislike with no reason counts as none.  Optional, because the room asked for a two-button screen and got one: the reason is a second, skippable tap. */
  reason_code?: VoteReason | null;
}

/** Answer the question a stalled party asked — phase 21, **ADR 0030**.  The one request in this file that is not about the game being played. A party stalls when nobody who may advance it is still here; after a grace period the room is asked whether to close it or keep waiting, and this is one person's answer.  ⚠️ **Absent players are not waited for**, so a client must never treat a missing answer as a pending one — the vote resolves on a strict majority of whoever is present, and an undecided window always means *keep waiting*. */
export interface StallVoteRequest {
  /** `true` closes the party; `false` keeps waiting.  Not an enum, because there are exactly two answers and there is no third one this could grow — a player who has not decided simply does not send this, which is a state the tally already distinguishes. */
  close: boolean;
}

/** Take a placed card off another side. */
export interface StealRequest {
  /** Whose card — a side id. The one place a request names somebody else, and it names the *target*, never the actor. */
  from: string;
  /** Which card, by track id. */
  track: string;
}

/** An acknowledgement. The state itself arrives on the party topic. */
export interface Ack {
  /** Always `true`. */
  ok: boolean;
}

/** Add a playlist to what this party deals from — phase 19e-2b. */
export interface AddPlaylistRequest {
  /** Whatever somebody pasted: a share link, a `spotify:` URI, or a bare id.  📌 Parsed server-side by `spotify::link`, which accepts every form a person can actually produce — including the `intl-es` locale segment a Spanish client puts in the middle of a share link. */
  link: string;
}

/** What the host may change in the lobby.  Every field optional and absent-means-unchanged, so the lobby can send one message per tap without reading back and re-sending everything it did not touch. */
export interface ConfigureRequest {
  /** Whether this party deals cards whose year is only **approximate** — ADR 0024. Defaults to **true**; `false` is strict (two agreeing sources only, everything else becomes an undated card). */
  allow_approximate_years?: boolean | null;
  /** How many cards win the game, counting the one every side starts with. */
  cards_to_win?: number | null;
  /** Replace the decades likewise. */
  decades?: number[] | null;
  /** `couch` · `remote`. */
  device_layout?: string | null;
  /** Replace the genres this party draws from — phase 16h.  Whole-list replace rather than a delta, like everything else here: the lobby sends what it wants to be true, which is one fewer way to be wrong. */
  genres?: string[] | null;
  /** `classic` · `deathmatch` · `team`. */
  mode?: string | null;
  /** Rename the party. */
  name?: string | null;
  /** `host` · `auto`. */
  pacing?: string | null;
  /** How many of every ten dealt cards may have no year — 0 to 10 (phase 19g). */
  wildcards_per_ten?: number | null;
}

/** Contribute, or stop contributing, your own liked songs. */
export interface ContributeLibraryRequest {
  /** In or out. */
  contribute: boolean;
}

/** Create a team. */
export interface CreateTeamRequest {
  /** Team name, unique within the party. */
  name: string;
}

/** Join a team, or leave one with `None`. */
export interface JoinTeamRequest {
  /** The team, or `None` to leave. */
  team_id?: string | null;
}

/** Somebody in the room. */
export interface LobbyMember {
  /** Name on the board. */
  display_name: string;
  /** The **profile** id — what every result and stat keys off, not an account. */
  profile_id: string;
  /** Whether they have said they are ready. */
  ready: boolean;
  /** `host` · `player` · `display`. */
  role: string;
  /** Team, in team mode. */
  team_id?: string | null;
}

/** One thing a party draws on, as the lobby sees it. */
export interface LobbySource {
  /** Which **profile** added it, so the screen can offer removal to the right person. `None` for a guest, who has no account, or for somebody who has since left the party.  🔴 **This was an account id until 19e-4, and the field could not do its stated job.** Every other id on this payload — `LobbyMember.profile_id`, and the `you` a client compares against — is a profile, so a screen holding an account id here knew only that *somebody* had added the source. `lobby.rs` translates it against this party's members. */
  added_by?: string | null;
  /** Their name, so a row can read "Ana's playlist" rather than an id.  Resolved server-side for the same reason `added_by` is: the roster this payload carries is keyed by profile, and a screen given an account could not join the two. */
  added_by_name?: string | null;
  /** How many will be dealt with a year at all, under this party's `allow_approximate_years`.  ⚠️ **Not the same question as `resolved`.** A track can be dated without being resolved — that is ADR 0024's rung three, Spotify's own year — so a source can read 8% resolved and 94% dated. The first says how much of this friend's music the catalog knows; the second says how much of it will grow a timeline. */
  dated: number;
  /** `playlist` or `library`. */
  kind: string;
  /** What to show: a playlist's name once it has been imported, and its id until then. **Never empty** — a source with no label renders as a blank row somebody cannot remove. */
  label: string;
  /** The playlist id, or the contributing account's id. */
  ref_id: string;
  /** How many of those this catalog has, through the ISRC join — phase 19e-4.  🎯 **Per contributor, never a party average.** The mix-of-everyone default lets one friend's obscure library dominate the undated ratio, and a single number hides exactly that: a host sees "38% dated" and cannot tell whether everybody is at 38% or three people are at 80% and one at 2%. */
  resolved: number;
  /** How many recordings it holds, as last imported. `0` until it has been — which is a real state a host meets, and the refresh button is the answer.  📌 **`songs`, not `tracks`**, and the name is load-bearing twice over: it is what the screen calls them, and `track` is one of the words the no-answer guard in this module's tests scans for. That guard exists because the lobby payload reaches a *display* (critical rule 7), and the cheapest way to keep it strict is to not need an exemption. */
  songs: number;
}

/** A team. */
export interface LobbyTeam {
  /** Team id. */
  id: string;
  /** Team name, unique within the party. */
  name: string;
}

/** One finished game in a party's series. */
export interface PlayedGame {
  /** When it ended, as an RFC 3339 timestamp. */
  finished_at: string;
  /** Which game — the recap's address, `/recap/:game_id`. */
  game_id: string;
  /** Which side won, if anybody did. `null` for a game that was abandoned or whose snapshot this build could not read. */
  winner_side?: string | null;
}

/** The whole lobby, as everyone sees it.  One payload for the initial state *and* every subsequent broadcast. A separate "delta" shape would be smaller and would double the number of ways a client can be wrong; a lobby is a dozen rows and this is sent when somebody taps something, not sixty times a second. */
export interface Lobby {
  /** Whether this party deals cards whose year is only **approximate** — ADR 0024. Defaults to **true**; `false` is strict (two agreeing sources only, everything else becomes an undated card). */
  allow_approximate_years: boolean;
  /** How many cards a complete timeline holds — phase 14 C2.  **Including the card every side starts with.** Ten means nine won, which is Hitster's rule and the one the first real game expected.  Always a number, never "unset": the column is nullable because a party created before phase 14 never chose, and resolving that here means the lobby renders one answer rather than a picker with nothing selected and a footnote about server defaults. */
  cards_to_win: number;
  /** The four-character room code. */
  code: string;
  /** The decades it draws from, as the first year of each: `1980`, `1990`.  **Empty means every decade.** Two entries is *"eighties and nineties"* — the thing `parties.deck_id` could not express, being a single reference. */
  decades: number[];
  /** `couch` · `remote`. */
  device_layout: string;
  /** The genres this party draws from — phase 16h.  **Empty means every genre**, which is the honest reading of "the host picked nothing" and is what makes an unconfigured party playable. A chosen family matches its subgenres. */
  genres: string[];
  /** Everybody present, hosts first then join order. */
  members: LobbyMember[];
  /** `classic` · `deathmatch` · `team`. */
  mode: string;
  /** What the host called it. */
  name?: string | null;
  /** How many **on-brief dated** cards the current selection admits.  So a host sees a choice is too thin **before** starting rather than after — the warning `DeckPicker` gave about a deck, kept because unioning is precisely how a host fixes it.  🔴 **This used to be one number called `playable_tracks`, and one number was misleading** (ADR 0025, phase 19e-4). A wildcard is rationed by `wildcards_per_ten`, so a deck of a thousand wildcards and four on-brief cards is not a deck of 1,004 — it is a game that runs out after four rounds. The sum reported that deck as healthy, so there is no sum: a caller that wants the total adds these two and has said so. */
  on_brief_tracks: number;
  /** Who moves the game on between rounds — `host` · `auto` (phase 14 C3). */
  pacing: string;
  /** Party id. */
  party_id: string;
  /** The games this party has already finished, oldest first — phase 16f-2.  **What makes a night of rematches read as a series** rather than as disconnected games. Empty for a party that has not played yet, which is most of them, so the lobby draws nothing.  Recaps are permanent and each already lives at its own `/recap/:gameId`, so nothing here expires — this is a list of addresses that already existed and that nothing had ever offered. */
  series: PlayedGame[];
  /** What this party draws on beyond the local catalog — phase 19e-2b, with its resolution counts since 19e-4.  📌 **The counts arrived with the screen that renders them**, which is why they were not here in 19e-3b even though the query was written then: a payload that grows fields ahead of the screen that needs them is how the answer eventually leaks. */
  sources: LobbySource[];
  /** `open` · `live` · `closed`. */
  status: string;
  /** Teams, in creation order. */
  teams: LobbyTeam[];
  /** **Where this is published.**  A push frame carries the topic's *hash* and nothing else, so a client matches by hashing the string it subscribed to — and a client that rebuilt that string from a remembered format stops matching the day the server changes it. [`super::game::Watching`] hands back its topic names for that reason (phase 5c); the lobby was the one place still guessing, with a comment in the TypeScript saying it mirrored the Rust.  Repeated on every broadcast rather than sent once, because this type is deliberately one payload for the initial state *and* every subsequent change — a separate "here is your topic" reply would be a second shape for one thing. */
  topic: string;
  /** How many cards it admits that sit **outside what the host asked for**.  Cards with no year (19g), and — in a party that narrowed its genres — Spotify recordings this catalog cannot attribute a genre to (19e-3b). Both are rationed out of `wildcards_per_ten`, which is why they are counted apart from the cards that pace the game. */
  wildcard_tracks: number;
  /** How many of every ten dealt cards may sit outside what the host asked for — phase 19g, widened by 19e-3b.  Two kinds share this one budget: a card with **no year** (19g), and — in a party that narrowed its genres — a Spotify recording the catalog has never heard of and so cannot attribute a genre to. They compete for the same allowance on purpose, so that narrowing a filter cannot quietly multiply what ignores it.  Resolved to a number here for the same reason `cards_to_win` is: the column is nullable, and a lobby should render one answer rather than a picker with nothing selected. */
  wildcards_per_ten: number;
}

/** Say whether you are ready to start. */
export interface ReadyRequest {
  /** Ready or not. */
  ready: boolean;
}

/** Take a source away. */
export interface RemoveSourceRequest {
  /** `playlist` or `library`. */
  kind: string;
  /** The playlist id, or the contributing account's id. */
  ref_id: string;
}

// =============================================================================
// Post-Quantum Session Lock (Phase 57)
// =============================================================================

// The server's lock static public key (hex), embedded at generation time. When a pin is in
// effect the client loads /spheromak/lock.js + /spheromak/lock.wasm, runs the hybrid
// X25519 + ML-KEM `NK` Noise handshake, and seals every frame — so a network/DevTools
// observer sees only ciphertext.
//
// This const is only the FALLBACK, and it is null whenever the client was generated without
// a live server (`auto_discover()` + `generate_typescript_client()`): the pin is per-server,
// so an offline generator cannot know which deployment this client will talk to. Supply it
// at runtime via `lockServerPublicKey`, or let the client ask the server for it
// (`lockDiscovery`, on by default). See resolveLockPin() for the precedence.
const LOCK_SERVER_PUBLIC_KEY: string | null = null;

/** The subset of the wasm lock (`spheromak-lock/dist/lock.d.ts`) the client drives. */
interface LockLike {
  isReady(): boolean;
  isMyTurn(): boolean;
  writeHandshake(): Uint8Array;
  readHandshake(message: Uint8Array): void;
  seal(plaintext: Uint8Array): Uint8Array;
  open(ciphertext: Uint8Array): Uint8Array;
}

/** The factory that mints an initiator lock pinning the server's static public key. */
interface LockFactory {
  newInitiator: (serverStaticPublic: Uint8Array) => LockLike;
}

/** The wasm-bindgen glue surface the client uses (dynamically imported from the server). */
interface LockModule {
  default: (init: { module_or_path: BufferSource }) => Promise<unknown>;
  WasmLock: LockFactory;
}

/** Decode the embedded hex pin to bytes. */
function hexToBytes(hex: string): Uint8Array {
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) {
    out[i] = parseInt(hex.substr(i * 2, 2), 16);
  }
  return out;
}

/** Validate a lock pin and normalise it to lowercase hex. Throws on anything that is not
 * exactly 32 bytes of hex — a malformed pin must fail loudly where it was written, not
 * decode to garbage and surface as an opaque handshake failure. */
function normalizeLockPin(pin: string, source: string): string {
  const hex = pin.trim().toLowerCase();
  if (!/^[0-9a-f]{64}$/.test(hex)) {
    throw new Error(
      `Invalid lock server public key from ${source}: expected 64 hex characters (32 bytes), got ${pin.length}`,
    );
  }
  return hex;
}

/** The public lock-discovery document served at GET /spheromak/lock.json. */
interface LockDiscoveryDocument {
  enabled?: boolean;
  publicKey?: string | null;
  integrity?: string | null;
}

// =============================================================================
// Client Class
// =============================================================================

export class SonolumeClient {
  private ws: WebSocket | null = null;
  private pendingRequests: Map<string, {
    resolve: (value: unknown) => void;
    reject: (error: Error) => void;
    timeout: ReturnType<typeof setTimeout>;
    /** When set, resolve with the raw payload bytes (binary auth frames). */
    raw?: boolean;
  }> = new Map();
  private pendingStreams: Map<string, {
    onChunk: (data: Uint8Array) => void;
    onEnd: () => void;
    onError: (error: Error) => void;
    /** Whether the server keeps a replay buffer for this stream, so it can be
     * resumed on a new connection instead of dying with the old one. */
    resumable: boolean;
    /** Chunks delivered so far — the client's implicit offset. The next chunk
     * expected is exactly this number, which is what a resume asks for. */
    received: number;
  }> = new Map();
  private sessionToken: Uint8Array | null = null;
  private connectionState: ConnectionState = 'disconnected';
  private credentials: AuthCredentials | null = null;
  private reconnectAttempts = 0;
  private options: Required<Omit<ConnectionOptions, 'lockLoader' | 'lockServerPublicKey'>>;
  // `| undefined` rather than `?`: under `exactOptionalPropertyTypes` an
  // optional property may not be *assigned* undefined, and the constructor does
  // exactly that when no loader was supplied.
  private lockLoader: (() => Promise<LockFactory>) | undefined;
  // Assigned in the constructor rather than declared as a parameter property,
  // which is TS-only syntax rejected under `erasableSyntaxOnly`.
  private url: string;
  /** Pin supplied at runtime (the strong, out-of-band form); null when none was given. */
  private lockPinOverride: string | null;
  private lockFactory: LockFactory | null = null;
  private eventListeners: Map<string, Set<(data: unknown) => void>> = new Map();
  private requestBuffer: BufferedRequest[] = [];
  private wasAuthenticated = false;
  // PQ session lock (Phase 57): null until the handshake completes (or forever, if the
  // server runs no lock). While `lockHandshaking`, inbound frames are Noise handshake
  // messages queued in `lockInbox` for the handshake driver, not application frames.
  private lock: LockLike | null = null;
  private lockHandshaking = false;
  private lockInbox: Uint8Array[] = [];
  private lockNotify: (() => void) | null = null;

  readonly catalog: CatalogController;
  readonly game: GameController;
  readonly party: PartyController;

  constructor(url: string, options: ConnectionOptions = {}) {
    this.url = url;
    this.options = {
      autoReconnect: options.autoReconnect ?? true,
      maxReconnectAttempts: options.maxReconnectAttempts ?? 5,
      reconnectDelay: options.reconnectDelay ?? 1000,
      timeout: options.timeout ?? 10000,
      subprotocols: options.subprotocols ?? [],
      bufferRequests: options.bufferRequests ?? true,
      maxBufferedRequests: options.maxBufferedRequests ?? 100,
      bufferTimeout: options.bufferTimeout ?? 300000,
      restoreSubscriptions: options.restoreSubscriptions ?? true,
      lockDiscovery: options.lockDiscovery ?? true,
      requireLock: options.requireLock ?? false,
    };
    this.lockLoader = options.lockLoader;
    // Validate eagerly: a typo in the pin should fail where it was written, not as an
    // opaque Noise error on the first connect.
    this.lockPinOverride = options.lockServerPublicKey
      ? normalizeLockPin(options.lockServerPublicKey, 'lockServerPublicKey')
      : null;
    this.catalog = new CatalogController(this);
    this.game = new GameController(this);
    this.party = new PartyController(this);
  }

  // ---------------------------------------------------------------------------
  // Connection Management
  // ---------------------------------------------------------------------------

  get state(): ConnectionState {
    return this.connectionState;
  }

  get isConnected(): boolean {
    return this.connectionState === 'connected' || this.connectionState === 'authenticated';
  }

  get isAuthenticated(): boolean {
    return this.connectionState === 'authenticated';
  }

  async connect(): Promise<void> {
    // 'reconnecting' is a legitimate entry state: handleClose sets it before
    // scheduling performReconnect, which comes straight back here. Rejecting it
    // made every automatic reconnect throw into a swallowed catch, so the client
    // never actually came back.
    if (this.connectionState !== 'disconnected' && this.connectionState !== 'reconnecting') {
      throw new Error(`Cannot connect: current state is ${this.connectionState}`);
    }

    this.connectionState = 'connecting';
    this.emit('stateChange', this.connectionState);

    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        this.ws?.close();
        reject(new Error('Connection timeout'));
      }, this.options.timeout);

      this.ws = this.options.subprotocols.length > 0
        ? new WebSocket(this.url, this.options.subprotocols)
        : new WebSocket(this.url);
      this.ws.binaryType = 'arraybuffer';

      this.ws.onopen = async () => {
        try {
          // Run the PQ-lock handshake BEFORE reporting connected, so a wrong pinned
          // server key surfaces as a connect error (not a later request timeout), and
          // every application frame that follows is sealed.
          const pin = await this.resolveLockPin();
          if (pin !== null) {
            await this.runLockHandshake(pin);
          } else if (this.options.requireLock) {
            throw new Error(
              'requireLock is set but no lock is in effect: no pin was supplied via ' +
              'lockServerPublicKey, none was embedded at generation time, and discovery ' +
              'did not report an enabled lock.',
            );
          }
          clearTimeout(timeoutId);
          // ⚠️ Do not overwrite 'authenticated'. A server with an upgrade gate
          // announces the admission as its FIRST frame, which can be processed
          // while this handler is still awaiting the lock handshake above —
          // `onmessage` is wired before any of it. Assigning 'connected'
          // unconditionally silently demoted a connection the carrier had
          // already authenticated, and the demotion is invisible: calls still
          // work (the server resolves the connection's session), while
          // `isAuthenticated` reads false forever.
          if (this.connectionState !== 'authenticated') {
            this.connectionState = 'connected';
          }
          this.reconnectAttempts = 0;
          this.emit('stateChange', this.connectionState);
          this.emit('connected', undefined);
          resolve();
        } catch (err) {
          clearTimeout(timeoutId);
          this.connectionState = 'disconnected';
          this.emit('stateChange', this.connectionState);
          try { this.ws?.close(); } catch { /* ignore */ }
          reject(err instanceof Error ? err : new Error(String(err)));
        }
      };

      this.ws.onerror = (e) => {
        clearTimeout(timeoutId);
        this.connectionState = 'disconnected';
        this.emit('stateChange', this.connectionState);
        reject(e);
      };

      this.ws.onmessage = (event) => this.handleMessage(event.data);
      this.ws.onclose = () => this.handleClose();
    });
  }

  disconnect(): void {
    this.options.autoReconnect = false;
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.sessionToken = null;
    this.connectionState = 'disconnected';
    this.emit('stateChange', this.connectionState);
  }

  /** Get the negotiated WebSocket subprotocol (after connection) */
  get protocol(): string | null {
    return this.ws?.protocol ?? null;
  }

  // ---------------------------------------------------------------------------
  // Event Handling
  // ---------------------------------------------------------------------------

  on(event: string, listener: (data: unknown) => void): () => void {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, new Set());
    }
    this.eventListeners.get(event)!.add(listener);
    return () => this.off(event, listener);
  }

  off(event: string, listener: (data: unknown) => void): void {
    this.eventListeners.get(event)?.delete(listener);
  }

  private emit(event: string, data: unknown): void {
    this.eventListeners.get(event)?.forEach(listener => listener(data));
  }

  // ---------------------------------------------------------------------------
  // PQ Session Lock (Phase 57; runtime pin resolution added in Phase 70)
  // ---------------------------------------------------------------------------

  /** Send one frame, sealing it through the lock when active. All application sends
   * route through here so sealing is a single choke point.
   *
   * `.buffer` is not decoration. TypeScript 5.7 made `Uint8Array` generic over its
   * backing buffer, and `WebSocket.send` takes `BufferSource` — which admits only
   * `ArrayBuffer`-backed views. A plain `Uint8Array` is `Uint8Array<ArrayBufferLike>`,
   * which might be `SharedArrayBuffer`-backed, so a consumer compiling the generated
   * client under `strict` gets an error on a line they cannot edit. Sending the
   * underlying `ArrayBuffer` sidesteps the variance without a cast that would paper
   * over a real distinction. */
  private sendFrame(data: Uint8Array | ArrayBuffer): void {
    const w = this.ws;
    if (!w) throw new Error('Not connected');
    if (this.lock) {
      const bytes = data instanceof Uint8Array ? data : new Uint8Array(data);
      const sealed = this.lock.seal(bytes);
      w.send(new Uint8Array(sealed).buffer);
    } else {
      w.send(data instanceof Uint8Array ? new Uint8Array(data).buffer : data);
    }
  }

  /** The origin serving this API, derived from the WebSocket URL. */
  private httpOrigin(): string {
    const u = new URL(this.url);
    const scheme = u.protocol === 'wss:' ? 'https:' : 'http:';
    return `${scheme}//${u.host}`;
  }

  /** Resolve the server pin to use for this connection, or null to speak plaintext.
   *
   * Precedence, strongest first:
   *   1. `lockServerPublicKey` — supplied out of band; an active attacker cannot influence it.
   *   2. `LOCK_SERVER_PUBLIC_KEY` — baked in when a live server generated this client, so it
   *      is exactly as trustworthy as the origin that served the client.
   *   3. discovery (`GET /spheromak/lock.json`) — trust-on-first-use, and the only option
   *      left for a client generated offline. On by default because the alternative is
   *      silently speaking plaintext to a server that requires the lock, which is how this
   *      failed before: the server closed the connection with `noise error` and nothing
   *      client-side ever said "your pin is null".
   *
   * Not cached: a server restarted with an ephemeral lock key has a new pin, and a
   * reconnect must pick it up. */
  private async resolveLockPin(): Promise<string | null> {
    if (this.lockPinOverride !== null) return this.lockPinOverride;
    if (LOCK_SERVER_PUBLIC_KEY !== null) {
      return normalizeLockPin(LOCK_SERVER_PUBLIC_KEY, 'the generated client');
    }
    if (!this.options.lockDiscovery) return null;

    let doc: LockDiscoveryDocument;
    try {
      const resp = await fetch(`${this.httpOrigin()}/spheromak/lock.json`);
      // A server too old to publish the document, or one behind a proxy that swallows the
      // route, is not an error: fall through to plaintext exactly as before. `requireLock`
      // is what turns that outcome into a failure.
      if (!resp.ok) return null;
      doc = await resp.json() as LockDiscoveryDocument;
    } catch {
      return null;
    }

    if (doc.enabled !== true || !doc.publicKey) return null;
    return normalizeLockPin(doc.publicKey, 'lock discovery');
  }

  /** Obtain the wasm lock factory (cached). Uses the injected `lockLoader` when provided,
   * otherwise dynamically imports the ES-module glue the server serves same-origin and
   * feeds it the fetched wasm bytes — browser-native, no bundler step. */
  private async loadLock(): Promise<LockFactory> {
    if (this.lockFactory) return this.lockFactory;
    if (this.lockLoader) {
      this.lockFactory = await this.lockLoader();
      return this.lockFactory;
    }
    const base = this.httpOrigin();
    const mod = await import(/* @vite-ignore */ `${base}/spheromak/lock.js`) as LockModule;
    const wasmResp = await fetch(`${base}/spheromak/lock.wasm`);
    if (!wasmResp.ok) throw new Error(`Failed to load lock.wasm: ${wasmResp.status}`);
    const wasmBytes = await wasmResp.arrayBuffer();
    await mod.default({ module_or_path: wasmBytes });
    this.lockFactory = mod.WasmLock;
    return this.lockFactory;
  }

  /** Drive the NK handshake to completion, then install the lock so subsequent frames
   * are sealed. The initiator (client) speaks first; inbound handshake messages are
   * delivered via `lockInbox` (see handleMessage). */
  private async runLockHandshake(pinHex: string): Promise<void> {
    const factory = await this.loadLock();
    const lock = factory.newInitiator(hexToBytes(pinHex));
    // Drop any lock from a previous connection so handshake frames are sent unsealed.
    this.lock = null;
    this.lockHandshaking = true;
    this.lockInbox = [];
    const nextMsg = (): Promise<Uint8Array> => new Promise((resolveMsg) => {
      const take = () => {
        const m = this.lockInbox.shift();
        if (m) { resolveMsg(m); } else { this.lockNotify = take; }
      };
      take();
    });
    try {
      while (!lock.isReady()) {
        if (lock.isMyTurn()) {
          this.sendFrame(lock.writeHandshake());
        } else {
          lock.readHandshake(await nextMsg());
        }
      }
    } finally {
      this.lockHandshaking = false;
      this.lockNotify = null;
    }
    this.lock = lock;
    // Any frames queued during the handshake tail are sealed application frames — process
    // them now through the normal (open + decode) path.
    const queued = this.lockInbox;
    this.lockInbox = [];
    for (const q of queued) this.dispatchFrame(q);
  }

  // ---------------------------------------------------------------------------
  // Authentication
  // ---------------------------------------------------------------------------

  /** Authenticate with the Ed25519 challenge-response strategy.
   *
   * Identity, not confidentiality — run this over `wss://` or the session lock.
   *
   * Resolves to `'mfaRequired'` when the credentials were accepted but the
   * server wants a second factor: finish with {@link completeMfa}.
   */
  async authenticate(credentials: AuthCredentials): Promise<AuthOutcome> {
    if (!this.isConnected) {
      throw new Error('Must be connected before authenticating');
    }
    this.connectionState = 'authenticating';
    this.emit('stateChange', this.connectionState);

    try {
      const ed = await loadEd25519();

      // Begin round: ask the server for an ed25519 challenge.
      const beginReply = await this.sendAuthFrame(
        MessageType.AuthChallenge,
        encodeAuthEnvelope('ed25519', new Uint8Array(0)),
      );
      const challenge = parseAuthChallenge(decodeAuthEnvelope(beginReply).payload);

      // Optional server-key pin.
      if (credentials.serverPublicKey && !bytesEqual(credentials.serverPublicKey, challenge.serverPublicKey)) {
        throw new Error('server key mismatch: challenge key differs from the pinned key');
      }

      // Verify the server's signature over nonce || timestamp.
      const serverSigned = concatBytes(challenge.nonce, u64beBytes(challenge.timestamp));
      const serverOk = await ed.verifyAsync(challenge.serverSignature, serverSigned, challenge.serverPublicKey);
      if (!serverOk) {
        throw new Error('server signature invalid');
      }

      // Sign nonce || timestamp || clientId and return an AuthResponse.
      const seed = credentials.privateKey.slice(0, 32);
      const clientPublicKey = await ed.getPublicKeyAsync(seed);
      const toSign = concatBytes(
        challenge.nonce,
        u64beBytes(challenge.timestamp),
        new TextEncoder().encode(credentials.clientId),
      );
      const signature = await ed.signAsync(toSign, seed);
      const response = encodeAuthResponse(
        credentials.clientId,
        clientPublicKey,
        signature,
        credentials.isRegistration === true,
      );

      const successBytes = await this.sendAuthFrame(
        MessageType.AuthResponse,
        encodeAuthEnvelope('ed25519', response),
      );
      this.applyAuthSuccess(successBytes);
      return 'authenticated';
    } catch (e) {
      // A failed handshake — or a pending second factor — leaves the
      // connection usable (unauthenticated), never stuck in 'authenticating'.
      this.connectionState = 'connected';
      this.emit('stateChange', this.connectionState);
      if (isMfaRequired(e)) return 'mfaRequired';
      throw e;
    }
  }

  /** Authenticate with a single-round strategy (JWT, API key, or a custom
   * bearer strategy) by sending its credentials under the strategy name.
   *
   * `strategy` must match a strategy the server registered; `credentials` is
   * the strategy-specific payload (a JWT string, an API key, …).
   *
   * Resolves to `'mfaRequired'` when the strategy's identity is covered by an
   * MFA policy — finish with {@link completeMfa}.
   */
  async authenticateWithToken(strategy: string, credentials: Uint8Array | string): Promise<AuthOutcome> {
    if (!this.isConnected) {
      throw new Error('Must be connected before authenticating');
    }
    const creds = typeof credentials === 'string' ? new TextEncoder().encode(credentials) : credentials;
    this.connectionState = 'authenticating';
    this.emit('stateChange', this.connectionState);

    try {
      // Begin round (the server acknowledges the strategy).
      await this.sendAuthFrame(MessageType.AuthChallenge, encodeAuthEnvelope(strategy, new Uint8Array(0)));
      // Verify round (send credentials).
      const successBytes = await this.sendAuthFrame(
        MessageType.AuthResponse,
        encodeAuthEnvelope(strategy, creds),
      );
      this.applyAuthSuccess(successBytes);
      return 'authenticated';
    } catch (e) {
      this.connectionState = 'connected';
      this.emit('stateChange', this.connectionState);
      if (isMfaRequired(e)) return 'mfaRequired';
      throw e;
    }
  }

  /** Submit a second factor after an `'mfaRequired'` outcome.
   *
   * The code travels under the reserved `mfa` strategy on the SAME connection
   * that passed the first factor — the server holds the pending first-factor
   * state against that connection, so a reconnect means starting over.
   *
   * Throws if the code is rejected or expired.
   */
  async completeMfa(code: string): Promise<void> {
    const outcome = await this.authenticateWithToken('mfa', code);
    if (outcome !== 'authenticated') {
      throw new Error('server asked for another factor after MFA completion');
    }
  }

  /** @internal Send an auth frame and await the server's raw reply payload. */
  private sendAuthFrame(messageType: number, payload: Uint8Array): Promise<Uint8Array> {
    return new Promise((resolve, reject) => {
      const messageId = spheromakMessageId();
      const timeout = setTimeout(() => {
        this.pendingRequests.delete(messageId);
        reject(new Error('Authentication timeout'));
      }, this.options.timeout);

      this.pendingRequests.set(messageId, {
        resolve: (value) => resolve(value as Uint8Array),
        reject,
        timeout,
        raw: true,
      });

      const message = this.encodeMessage({
        version: PROTOCOL_VERSION,
        messageType,
        flags: 0,
        messageId,
        controllerHash: 0n,
        methodHash: 0n,
        payload,
      });
      this.sendFrame(message);
    });
  }

  /** @internal Apply an AuthSuccess (session_token ‖ session_id) and mark
   * the connection authenticated. */
  private applyAuthSuccess(bytes: Uint8Array): void {
    if (bytes.length < 48) {
      throw new Error('malformed AuthSuccess payload');
    }
    this.sessionToken = bytes.slice(0, 32);
    // `isAuthenticated` is a getter derived from connectionState.
    this.connectionState = 'authenticated';
    this.emit('stateChange', this.connectionState);
    this.emit('authenticated', undefined);
  }

  /** @internal The carrier authenticated this connection: an upgrade gate
   * admitted it during the handshake. The payload is the session **id** (16
   * bytes), which is not a credential.
   *
   * 🔴 No `sessionToken` is set, and that is the difference from
   * {@link applyAuthSuccess} rather than an omission. The credential lives with
   * the carrier — an `HttpOnly` cookie, a header a proxy injects — deliberately
   * out of this page's reach, and it is re-presented on every reconnect. Storing
   * a resumable token here would put in JavaScript exactly what forward-auth
   * took out of it.
   *
   * Requests therefore travel without `MessageFlags.AUTHENTICATED`, which is
   * correct: with no token on the frame the server resolves the session bound to
   * the connection, which is this one. */
  private applyCarrierAuth(sessionId: Uint8Array): void {
    if (sessionId.length !== 16) {
      // Ignored rather than thrown: an unrecognised control frame must not take
      // down a connection that is working.
      return;
    }
    if (this.connectionState === 'disconnected') {
      return;
    }
    this.connectionState = 'authenticated';
    this.emit('stateChange', this.connectionState);
    this.emit('authenticated', undefined);
  }

  // ---------------------------------------------------------------------------
  // Request Handling
  // ---------------------------------------------------------------------------

  private handleMessage(data: ArrayBuffer): void {
    const bytes = new Uint8Array(data);
    // During the lock handshake, inbound frames are Noise handshake messages — hand them
    // to the handshake driver (via lockInbox), not the application dispatch.
    if (this.lockHandshaking) {
      this.lockInbox.push(bytes);
      const n = this.lockNotify;
      this.lockNotify = null;
      if (n) n();
      return;
    }
    this.dispatchFrame(bytes);
  }

  /** Open a (possibly sealed) inbound frame and dispatch it to the waiting request/stream. */
  private dispatchFrame(bytes: Uint8Array): void {
    let payload = bytes;
    if (this.lock) {
      try {
        payload = this.lock.open(bytes);
      } catch {
        // A frame that fails to open is a forged/tampered/replayed frame — close the
        // connection rather than process it.
        this.ws?.close();
        return;
      }
    }
    const message = this.decodeMessage(payload);

    // Stream frames share their call's message ID with the pendingRequests
    // entry that awaits the call's FINAL response (client-streaming uploads),
    // so only genuine terminal messages may consume that entry.
    const isStreamFrame =
      message.messageType === MessageType.StreamStart ||
      message.messageType === MessageType.StreamChunk ||
      message.messageType === MessageType.StreamEnd ||
      message.messageType === MessageType.StreamError;
    const pending = isStreamFrame ? undefined : this.pendingRequests.get(message.messageId);

    if (pending) {
      clearTimeout(pending.timeout);
      this.pendingRequests.delete(message.messageId);

      if (message.messageType === MessageType.Error) {
        // Error frames carry a BINARY payload, never MessagePack — decoding
        // them with the payload codec loses the code (see decodeErrorPayload).
        const error = decodeErrorPayload(message.payload);
        pending.reject(
          error
            ? SpheromakApiError.fromError(error)
            : new SpheromakApiError('UNKNOWN', 'malformed error payload')
        );
      } else if (pending.raw || message.messageType === MessageType.AuthChallenge) {
        // Auth frames carry BINARY payloads (envelopes, AuthSuccess) that must
        // not be msgpack-decoded — resolve the raw bytes for the auth flow.
        pending.resolve(message.payload);
      } else {
        try {
          const result = decode(message.payload);
          pending.resolve(result);
        } catch (e) {
          pending.reject(new Error(`Failed to decode response: ${e}`));
        }
      }
    }

    // An Error frame can also address a STREAM (a refused StreamResume is
    // answered with the stream's id). Without this the consumer would wait
    // forever for a replay that is never coming.
    if (!pending && message.messageType === MessageType.Error) {
      const stream = this.pendingStreams.get(message.messageId);
      if (stream) {
        const error = decodeErrorPayload(message.payload);
        stream.onError(
          error
            ? SpheromakApiError.fromError(error)
            : new SpheromakApiError('UNKNOWN', 'malformed error payload')
        );
        this.pendingStreams.delete(message.messageId);
      }
      return;
    }

    // Handle stream messages
    if (message.messageType === MessageType.StreamStart) {
      const stream = this.pendingStreams.get(message.messageId);
      if (stream) {
        // Stream confirmed - already handling via onChunk/onEnd
      }
      this.emit('streamStart', { messageId: message.messageId });
    } else if (message.messageType === MessageType.StreamChunk) {
      const stream = this.pendingStreams.get(message.messageId);
      if (stream) {
        stream.onChunk(message.payload);
        // Count only chunks actually handed over: the count IS the resume
        // offset, so miscounting means a duplicate or a gap after a resume.
        stream.received += 1;
      }
      this.emit('streamChunk', { messageId: message.messageId, data: message.payload });
    } else if (message.messageType === MessageType.StreamEnd) {
      const stream = this.pendingStreams.get(message.messageId);
      if (stream) {
        stream.onEnd();
        this.pendingStreams.delete(message.messageId);
      }
      this.emit('streamEnd', { messageId: message.messageId });
    } else if (message.messageType === MessageType.StreamError) {
      const stream = this.pendingStreams.get(message.messageId);
      if (stream) {
        stream.onError(new Error(new TextDecoder().decode(message.payload)));
        this.pendingStreams.delete(message.messageId);
      }
      this.emit('streamError', { messageId: message.messageId });
    } else if (message.messageType === MessageType.Push) {
      // Server push (pub/sub fan-out, or a direct send). The frame carries the topic's
      // HASH in controllerHash, not its name — `Message::push` packs it there — so a
      // subscriber matches by hashing the topic it subscribed to. Until Phase 65 this
      // arm did not exist and every push was silently dropped.
      this.emit('push', {
        topicHash: message.controllerHash,
        payload: message.payload,
        decode: () => decode(message.payload),
      });
    } else if (message.messageType === MessageType.AuthEstablished) {
      // 🔴 The carrier already authenticated this connection — an upgrade gate
      // admitted it during the handshake, from a credential this client could
      // not have sent and cannot observe (a header a browser may not set on a
      // WebSocket, injected by a proxy). Without this arm the connection is
      // authenticated on the server and believes itself anonymous here, and
      // every `requiresAuth` call is refused BEFORE it is sent — a refusal the
      // server never sees and cannot contradict.
      this.applyCarrierAuth(message.payload);
    }
  }

  /** Whether a {@link PushEvent} was published to `topic`. */
  static isPushTopic(event: PushEvent, topic: string): boolean {
    return controllerHash(topic) === event.topicHash;
  }

  private bufferRequest(messageData: ArrayBuffer, resolve: (value: unknown) => void, reject: (error: Error) => void): boolean {
    if (!this.options.bufferRequests) return false;
    if (this.requestBuffer.length >= this.options.maxBufferedRequests) return false;

    this.requestBuffer.push({
      messageData,
      resolve,
      reject,
      bufferedAt: Date.now(),
    });
    return true;
  }

  private replayBufferedRequests(): void {
    const now = Date.now();
    const requests = this.requestBuffer.splice(0);
    for (const req of requests) {
      if (now - req.bufferedAt > this.options.bufferTimeout) {
        req.reject(new Error('Buffered request timeout'));
        continue;
      }
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.sendFrame(req.messageData);
      } else {
        req.reject(new Error('Not connected'));
      }
    }
  }

  private rejectAllBuffered(error: Error): void {
    const requests = this.requestBuffer.splice(0);
    for (const req of requests) {
      req.reject(error);
    }
  }

  /** @internal */
  async sendRequest<T>(controllerHash: bigint, methodHash: bigint, payload: unknown, requiresAuth: boolean): Promise<T> {
    // 🔴 No local auth refusal here, and its removal was a correctness fix.
    //
    // `isAuthenticated` is a CACHE of a fact only the server holds, and it can
    // be false on a connection the server has already authenticated: an upgrade
    // gate admits during the handshake, from a credential this client never saw
    // and could not have sent. The server announces that with an
    // `AuthEstablished` frame, but a client that refused *locally* the moment a
    // method was called could refuse before the frame landed — and that refusal
    // never leaves the browser, so no server log ever contradicts it. That is a
    // failure whose evidence is nowhere.
    //
    // Letting the call go costs one round trip when the caller really is
    // unauthenticated, and buys an answer from the only party that knows.
    // Nothing is weakened: `requires_auth` is enforced server-side on every
    // request and every stream, and what comes back is a structured
    // `SpheromakApiError` with `AUTH_REQUIRED` rather than a bare `Error`.

    const messageId = spheromakMessageId();
    const payloadData = encode(payload);
    const payloadBytes = payloadData instanceof Uint8Array ? payloadData : new Uint8Array(payloadData);

    const message = this.encodeMessage({
      version: PROTOCOL_VERSION,
      messageType: MessageType.Request,
      flags: requiresAuth && this.sessionToken ? MessageFlags.AUTHENTICATED : 0,
      messageId,
      controllerHash,
      methodHash,
      payload: payloadBytes,
    });

    // If not connected, try to buffer the request
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return new Promise((resolve, reject) => {
        const buffered = this.bufferRequest(message, (value) => resolve(value as T), reject);
        if (!buffered) {
          reject(new Error('Not connected and request buffering is disabled or full'));
        }
      });
    }

    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        this.pendingRequests.delete(messageId);
        reject(new Error('Request timeout'));
      }, this.options.timeout);

      this.pendingRequests.set(messageId, {
        resolve: (value) => resolve(value as T),
        reject,
        timeout,
      });
      this.sendFrame(message);
    });
  }

  // ---------------------------------------------------------------------------
  // Streaming Support
  // ---------------------------------------------------------------------------

  /** @internal Start a streaming request that returns an async iterator.
   *
   * With `resumable`, the server keeps a bounded replay buffer for this call:
   * losing the connection does not end the iterator, and reconnecting replays
   * exactly the chunks that were missed (no gap, no duplicate). */
  async *streamRequest<T>(controllerHash: bigint, methodHash: bigint, payload: unknown, requiresAuth: boolean, resumable = false): AsyncGenerator<T> {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      throw new Error('Not connected');
    }

    // No local auth refusal — the server is the only party that knows, and a
    // carrier-authenticated connection looks unauthenticated here until its
    // `AuthEstablished` frame lands. See `sendRequest` for the full reasoning.

    const messageId = spheromakMessageId();
    const payloadData = encode(payload);
    const payloadBytes = payloadData instanceof Uint8Array ? payloadData : new Uint8Array(payloadData);

    // Chunk queue + wakeup: frames are buffered here by handleMessage and
    // drained by the yield loop below (drain-before-end, no spurious items).
    const chunks: Uint8Array[] = [];
    let wake: (() => void) | null = null;
    let streamError: Error | null = null;
    let streamEnded = false;
    const notify = () => {
      if (wake) {
        const w = wake;
        wake = null;
        w();
      }
    };

    // Register stream handler
    this.pendingStreams.set(messageId, {
      onChunk: (data: Uint8Array) => {
        chunks.push(data);
        notify();
      },
      onEnd: () => {
        streamEnded = true;
        notify();
      },
      onError: (error: Error) => {
        streamError = error;
        streamEnded = true;
        notify();
      },
      resumable,
      received: 0,
    });

    // Send stream start message
    const message = this.encodeMessage({
      version: PROTOCOL_VERSION,
      messageType: MessageType.StreamStart,
      flags: (requiresAuth && this.sessionToken ? MessageFlags.AUTHENTICATED : 0) | MessageFlags.STREAM | (resumable ? MessageFlags.RESUMABLE : 0),
      messageId,
      controllerHash,
      methodHash,
      payload: payloadBytes,
    });
    this.sendFrame(message);

    // Drain chunks as they arrive; surface errors after the buffered items.
    try {
      while (true) {
        if (chunks.length > 0) {
          const data = chunks.shift()!;
          const decoded = decode(data) as T;
          yield decoded;
          continue;
        }
        if (streamError) throw streamError;
        if (streamEnded) break;
        await new Promise<void>((resolve) => {
          wake = resolve;
        });
      }
    } finally {
      this.pendingStreams.delete(messageId);
    }
  }

  /** @internal Resume every resumable stream still open, on the current
   * connection. Called after a reconnect; each stream resumes from the number
   * of chunks its consumer has already received. */
  private resumeOpenStreams(): void {
    for (const [streamId, stream] of this.pendingStreams) {
      if (!stream.resumable) continue;
      // StreamResumePayload: stream_id (16 bytes) + next expected offset
      // (u64, big-endian). The offset is INCLUSIVE — it is the next chunk
      // wanted, so a client that received nothing can ask for offset 0.
      const payload = new Uint8Array(24);
      payload.set(this.uuidToBytes(streamId), 0);
      new DataView(payload.buffer).setBigUint64(16, BigInt(stream.received), false);
      this.sendFrame(this.encodeMessage({
        version: PROTOCOL_VERSION,
        messageType: MessageType.StreamResume,
        flags: MessageFlags.STREAM,
        messageId: streamId,
        controllerHash: 0n,
        methodHash: 0n,
        payload,
      }));
    }
  }

  /** @internal Build the upload half of a streaming call: StreamChunk frames +
   * StreamEnd on `messageId`. When `finalResponse` is given (client-streaming
   * calls), `finish()` ends the stream and awaits the typed response. */
  private createUploadHandle(messageId: string, finalResponse: Promise<unknown> | null): UploadStream {
    let bytesSent = 0;
    let isEnded = false;
    const ws = this.ws;
    // Route the upload half's sends through the same sealing choke point as the rest.
    const sendFrame = this.sendFrame.bind(this);
    const encodeMessage = this.encodeMessage.bind(this);
    const pendingRequests = this.pendingRequests;
    const timeoutMs = this.options.timeout;
    const encodePayload = (payload: unknown) => {
      const data = encode(payload);
      return data instanceof Uint8Array ? data : new Uint8Array(data);
    };
    const sendEnd = () => {
      const endMessage = encodeMessage({
        version: PROTOCOL_VERSION,
        messageType: MessageType.StreamEnd,
        flags: MessageFlags.STREAM,
        messageId,
        controllerHash: 0n,
        methodHash: 0n,
        payload: new Uint8Array(0),
      });
      sendFrame(endMessage);
      isEnded = true;
    };

    return {
      get streamId() { return messageId; },
      get bytesSent() { return bytesSent; },
      get isEnded() { return isEnded; },

      send: async (data: Uint8Array): Promise<void> => {
        if (isEnded) throw new Error('Stream already ended');
        if (!ws || ws.readyState !== WebSocket.OPEN) throw new Error('Not connected');

        const chunkMessage = encodeMessage({
          version: PROTOCOL_VERSION,
          messageType: MessageType.StreamChunk,
          flags: MessageFlags.STREAM,
          messageId,
          controllerHash: 0n,
          methodHash: 0n,
          payload: data,
        });
        sendFrame(chunkMessage);
        bytesSent += data.length;
      },

      sendItem: async (item: unknown): Promise<void> => {
        if (isEnded) throw new Error('Stream already ended');
        const data = encodePayload(item);
        const chunkMessage = encodeMessage({
          version: PROTOCOL_VERSION,
          messageType: MessageType.StreamChunk,
          flags: MessageFlags.STREAM,
          messageId,
          controllerHash: 0n,
          methodHash: 0n,
          payload: data,
        });
        sendFrame(chunkMessage);
        bytesSent += data.length;
      },

      end: async (): Promise<number> => {
        if (isEnded) throw new Error('Stream already ended');
        sendEnd();
        // Fire-and-forget completion: nobody will await the final response.
        pendingRequests.delete(messageId);
        return bytesSent;
      },

      finish: async (): Promise<unknown> => {
        if (!finalResponse) {
          throw new Error('finish() is only available on client-streaming calls; bidirectional calls complete via the download stream');
        }
        if (isEnded) throw new Error('Stream already ended');
        sendEnd();
        return await new Promise((resolve, reject) => {
          const t = setTimeout(() => {
            pendingRequests.delete(messageId);
            reject(new Error('Request timeout'));
          }, timeoutMs);
          finalResponse.then(
            (value) => { clearTimeout(t); resolve(value); },
            (error) => { clearTimeout(t); reject(error); },
          );
        });
      },

      cancel: (): void => {
        if (isEnded) return;
        sendEnd();
        pendingRequests.delete(messageId);
      },
    };
  }

  /** @internal Start an upload (client-streaming) call. The server's final
   * typed response resolves through the returned handle's `finish()`. */
  startUpload(controllerHash: bigint, methodHash: bigint, payload: unknown, requiresAuth: boolean): UploadStream {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      throw new Error('Not connected');
    }

    // No local auth refusal — the server is the only party that knows, and a
    // carrier-authenticated connection looks unauthenticated here until its
    // `AuthEstablished` frame lands. See `sendRequest` for the full reasoning.

    const messageId = spheromakMessageId();
    const metadataData = encode(payload);
    const metadataBytes = metadataData instanceof Uint8Array ? metadataData : new Uint8Array(metadataData);

    // Register the final-response waiter BEFORE sending, so the server's
    // Response (or an early rejection Error) cannot race registration.
    let finishResolve: (value: unknown) => void = () => {};
    let finishReject: (error: Error) => void = () => {};
    const finalResponse = new Promise<unknown>((resolve, reject) => {
      finishResolve = resolve;
      finishReject = reject;
    });
    // Avoid an unhandled rejection when the caller uses end() instead of finish().
    finalResponse.catch(() => {});
    this.pendingRequests.set(messageId, {
      resolve: finishResolve,
      reject: finishReject,
      timeout: setTimeout(() => {}, 0),
    });

    // Send stream start message
    const startMessage = this.encodeMessage({
      version: PROTOCOL_VERSION,
      messageType: MessageType.StreamStart,
      flags: (requiresAuth && this.sessionToken ? MessageFlags.AUTHENTICATED : 0) | MessageFlags.STREAM,
      messageId,
      controllerHash,
      methodHash,
      payload: metadataBytes,
    });
    this.sendFrame(startMessage);

    return this.createUploadHandle(messageId, finalResponse);
  }

  /** @internal Start a bidirectional streaming call: ONE StreamStart opens the
   * call; the upload half and the typed download stream share its message ID.
   * The call completes when the download stream ends (or errors). */
  startBidirectional<TDown>(controllerHash: bigint, methodHash: bigint, payload: unknown, requiresAuth: boolean): { upload: UploadStream, download: AsyncGenerator<TDown> } {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      throw new Error('Not connected');
    }

    // No local auth refusal — the server is the only party that knows, and a
    // carrier-authenticated connection looks unauthenticated here until its
    // `AuthEstablished` frame lands. See `sendRequest` for the full reasoning.

    const messageId = spheromakMessageId();
    const metadataData = encode(payload);
    const metadataBytes = metadataData instanceof Uint8Array ? metadataData : new Uint8Array(metadataData);

    // Downstream consumer state — registered BEFORE the StreamStart is sent.
    const chunks: Uint8Array[] = [];
    let wake: (() => void) | null = null;
    let streamError: Error | null = null;
    let streamEnded = false;
    const notify = () => {
      if (wake) {
        const w = wake;
        wake = null;
        w();
      }
    };

    this.pendingStreams.set(messageId, {
      onChunk: (data: Uint8Array) => {
        chunks.push(data);
        notify();
      },
      onEnd: () => {
        streamEnded = true;
        notify();
      },
      onError: (error: Error) => {
        streamError = error;
        streamEnded = true;
        notify();
      },
      // Bidirectional calls are not resumable: the upload half's position
      // cannot be reconstructed from a replay buffer the server keeps for the
      // DOWNLOAD half alone.
      resumable: false,
      received: 0,
    });

    // ONE StreamStart for the whole bidirectional call.
    const startMessage = this.encodeMessage({
      version: PROTOCOL_VERSION,
      messageType: MessageType.StreamStart,
      flags: (requiresAuth && this.sessionToken ? MessageFlags.AUTHENTICATED : 0) | MessageFlags.STREAM,
      messageId,
      controllerHash,
      methodHash,
      payload: metadataBytes,
    });
    this.sendFrame(startMessage);

    const pendingStreams = this.pendingStreams;
    const download = (async function* (): AsyncGenerator<TDown> {
      try {
        while (true) {
          if (chunks.length > 0) {
            const data = chunks.shift()!;
            const decoded = decode(data) as TDown;
            yield decoded;
            continue;
          }
          if (streamError) throw streamError;
          if (streamEnded) break;
          await new Promise<void>((resolve) => {
            wake = resolve;
          });
        }
      } finally {
        pendingStreams.delete(messageId);
      }
    })();

    const upload = this.createUploadHandle(messageId, null);
    return { upload, download };
  }

  private handleClose(): void {
    this.wasAuthenticated = this.connectionState === 'authenticated';
    this.connectionState = 'disconnected';
    // Drop the lock; a reconnect re-runs the handshake and installs a fresh one.
    this.lock = null;
    // Do NOT clear sessionToken here - needed for session resume

    // Reject pending requests (unless buffering is enabled)
    for (const [_, pending] of this.pendingRequests) {
      clearTimeout(pending.timeout);
      pending.reject(new Error('Connection closed'));
    }
    this.pendingRequests.clear();

    // Settle streams. RESUMABLE ones are kept: their consumer must stay open
    // across the gap so the resume can continue feeding it. The rest are over.
    for (const [id, stream] of this.pendingStreams) {
      if (stream.resumable) continue;
      stream.onError(new Error('Connection closed'));
      this.pendingStreams.delete(id);
    }

    this.emit('stateChange', this.connectionState);
    this.emit('disconnected', { wasAuthenticated: this.wasAuthenticated });

    if (this.options.autoReconnect && this.reconnectAttempts < this.options.maxReconnectAttempts) {
      this.reconnectAttempts++;
      this.connectionState = 'reconnecting';
      this.emit('stateChange', this.connectionState);
      this.emit('reconnecting', { attempt: this.reconnectAttempts });

      setTimeout(() => {
        this.performReconnect().catch(() => {});
      }, this.options.reconnectDelay * this.reconnectAttempts);
    } else if (this.reconnectAttempts >= this.options.maxReconnectAttempts) {
      this.rejectAllBuffered(new Error('Max reconnect attempts exceeded'));
      this.emit('reconnectFailed', { attempts: this.reconnectAttempts });
    }
  }

  private async performReconnect(): Promise<void> {
    try {
      await this.connect();
      let sessionResumed = false;

      // Try session resume first
      if (this.wasAuthenticated && this.sessionToken) {
        sessionResumed = await this.trySessionResume();
      }

      // Resume every resumable stream still open, from the chunk offset its
      // consumer has already seen — the server replays exactly the gap.
      this.resumeOpenStreams();

      // Fall back to fresh auth if resume failed. An MFA-covered identity
      // cannot re-authenticate unattended — the pending first-factor state
      // lives on the new connection, so the app must prompt for a code and
      // call completeMfa(). Surface it rather than silently half-authenticating.
      if (!sessionResumed && this.wasAuthenticated && this.credentials) {
        const outcome = await this.authenticate(this.credentials);
        if (outcome === 'mfaRequired') {
          this.emit('mfaRequired', undefined);
        }
      }

      this.emit('reconnected', { sessionResumed });

      // Replay buffered requests
      this.replayBufferedRequests();
    } catch {
      // The attempt failed before a socket existed, so no close event will
      // arrive to drive the next one — schedule it here. Leaving the state at
      // 'reconnecting' with nothing pending would strand the client forever.
      this.connectionState = 'disconnected';
      this.emit('stateChange', this.connectionState);
      this.handleClose();
    }
  }

  private async trySessionResume(): Promise<boolean> {
    if (!this.sessionToken) return false;

    try {
      const payload = new Uint8Array(33);
      payload.set(this.sessionToken, 0);
      payload[32] = this.options.restoreSubscriptions ? 1 : 0;

      const messageId = spheromakMessageId();
      const message = this.encodeMessage({
        version: PROTOCOL_VERSION,
        messageType: MessageType.SessionResume,
        flags: 0,
        messageId,
        controllerHash: 0n,
        methodHash: 0n,
        payload,
      });

      return new Promise<boolean>((resolve) => {
        const timeout = setTimeout(() => {
          this.pendingRequests.delete(messageId);
          resolve(false);
        }, this.options.timeout);

        this.pendingRequests.set(messageId, {
          resolve: (_value) => {
            this.connectionState = 'authenticated';
            this.emit('stateChange', this.connectionState);
            this.emit('authenticated', undefined);
            resolve(true);
          },
          reject: () => resolve(false),
          timeout,
        });

        this.sendFrame(message);
      });
    } catch {
      return false;
    }
  }

  // ---------------------------------------------------------------------------
  // Protocol Encoding/Decoding
  // ---------------------------------------------------------------------------

  private encodeMessage(msg: SpheromakMessage): ArrayBuffer {
    const hasAuth = (msg.flags & MessageFlags.AUTHENTICATED) !== 0;
    const headerSize = 40 + (hasAuth ? 32 : 0);
    const buffer = new ArrayBuffer(headerSize + msg.payload.length);
    const view = new DataView(buffer);
    const bytes = new Uint8Array(buffer);

    let offset = 0;
    view.setUint8(offset++, msg.version);
    view.setUint8(offset++, msg.messageType);
    view.setUint16(offset, msg.flags); offset += 2;
    view.setUint32(offset, msg.payload.length); offset += 4;

    const uuidBytes = this.uuidToBytes(msg.messageId);
    bytes.set(uuidBytes, offset); offset += 16;

    if (hasAuth && this.sessionToken) {
      bytes.set(this.sessionToken, offset); offset += 32;
    }

    view.setBigUint64(offset, msg.controllerHash); offset += 8;
    view.setBigUint64(offset, msg.methodHash); offset += 8;

    bytes.set(msg.payload, offset);

    return buffer;
  }

  private decodeMessage(data: Uint8Array): SpheromakMessage {
    const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
    let offset = 0;

    const version = view.getUint8(offset++);
    const messageType = view.getUint8(offset++);
    const flags = view.getUint16(offset); offset += 2;
    const payloadLen = view.getUint32(offset); offset += 4;

    const uuidBytes = data.slice(offset, offset + 16); offset += 16;
    const messageId = this.bytesToUuid(uuidBytes);

    const hasAuth = (flags & MessageFlags.AUTHENTICATED) !== 0;
    if (hasAuth) offset += 32;

    const controllerHash = view.getBigUint64(offset); offset += 8;
    const methodHash = view.getBigUint64(offset); offset += 8;

    // `payloadLen` is the length *on the wire*, so it is the compressed length
    // when the flag is set. Slice first, then expand.
    const framed = data.slice(offset, offset + payloadLen);
    const payload = (flags & MessageFlags.COMPRESSED) !== 0 ? lz4Expand(framed) : framed;

    return { version, messageType, flags, messageId, controllerHash, methodHash, payload };
  }

  private uuidToBytes(uuid: string): Uint8Array {
    const hex = uuid.replace(/-/g, '');
    const bytes = new Uint8Array(16);
    for (let i = 0; i < 16; i++) {
      bytes[i] = parseInt(hex.substring(i * 2, i * 2 + 2), 16);
    }
    return bytes;
  }

  private bytesToUuid(bytes: Uint8Array): string {
    const hex = Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }
}

// =============================================================================
// LZ4 block decompression
// =============================================================================

// The server compresses any payload over `compression_threshold` (1 KiB by
// default, and compression is **on** by default) and sets
// `MessageFlags::COMPRESSED`. A client that ignores the flag hands LZ4 bytes to
// msgpack, which reports something like
// `RangeError: Extra 2652 of 2672 byte(s) found at buffer[20]` — a message that
// names neither compression nor the flag.
//
// Nothing smaller than the threshold is affected, so a surface can work for
// months and then break the first time a list grows past a kilobyte. That is how
// this was found: divertor's portal worked screen by screen until its audit log
// returned fifty rows (2026-08-07).
//
// The wire form is `lz4_flex::compress_prepend_size`: a 4-byte little-endian
// uncompressed length, then a raw LZ4 **block** (not a frame — no magic number,
// no block checksums).

/// Mirrors `MAX_DECOMPRESSED_SIZE` in `spheromak-protocol`. The length prefix
/// arrives from the network, so it is a claim rather than a fact: without a
/// bound, one small message can ask a browser tab for gigabytes.
const LZ4_MAX_DECOMPRESSED = 64 * 1024 * 1024;

/// Expand a `compress_prepend_size` payload.
const lz4Expand = (input: Uint8Array): Uint8Array => {
  if (input.length < 4) {
    throw new Error(`compressed payload is ${input.length} bytes, too short to carry its length`);
  }
  const view = new DataView(input.buffer, input.byteOffset, input.byteLength);
  const expanded = view.getUint32(0, true);
  if (expanded > LZ4_MAX_DECOMPRESSED) {
    throw new Error(`compressed payload declares ${expanded} bytes, over the ${LZ4_MAX_DECOMPRESSED} limit`);
  }

  const src = input.subarray(4);
  const out = new Uint8Array(expanded);
  let sp = 0;
  let op = 0;

  // Every extended length is a run of 0xFF bytes terminated by anything else.
  const extend = (length: number): number => {
    let more = 0;
    do {
      if (sp >= src.length) throw new Error('compressed payload ended inside a length');
      more = src[sp++]!;
      length += more;
    } while (more === 255);
    return length;
  };

  while (sp < src.length) {
    const token = src[sp++]!;

    let literals = token >> 4;
    if (literals === 15) literals = extend(literals);
    if (sp + literals > src.length || op + literals > out.length) {
      throw new Error('compressed payload overruns on its literals');
    }
    out.set(src.subarray(sp, sp + literals), op);
    sp += literals;
    op += literals;

    // The final sequence is literals only: no match follows it.
    if (sp >= src.length) break;

    const offset = src[sp]! | (src[sp + 1]! << 8);
    sp += 2;
    if (offset === 0 || offset > op) {
      throw new Error(`compressed payload references ${offset} bytes back, outside what it has produced`);
    }

    let match = token & 0x0f;
    if (match === 15) match = extend(match);
    match += 4; // LZ4's minimum match length.
    if (op + match > out.length) {
      throw new Error('compressed payload overruns on a match');
    }

    // Byte at a time on purpose: LZ4 matches are allowed to overlap the region
    // they are being written into (that is how it encodes runs), so a bulk copy
    // of the source range would read bytes that have not been written yet.
    let from = op - offset;
    for (let i = 0; i < match; i++) out[op++] = out[from++]!;
  }

  if (op !== expanded) {
    throw new Error(`compressed payload expanded to ${op} bytes, not the ${expanded} it declared`);
  }
  return out;
};

// =============================================================================
// Topic hashing (xxHash64)
// =============================================================================

// Controller paths and pub/sub topics travel as an xxHash64 of the string, never the
// string itself (`ControllerHash::from_path`, seed 0). A client that receives a push
// therefore needs the same hash to tell which topic it arrived on — without this, a
// browser subscriber cannot match a push to a topic at all.
//
// Must stay byte-for-byte compatible with `xxhash_rust::xxh64`.

const XXH_MASK = 0xFFFFFFFFFFFFFFFFn;
const XXH_P1 = 11400714785074694791n;
const XXH_P2 = 14029467366897019727n;
const XXH_P3 = 1609587929392839161n;
const XXH_P4 = 9650029242287828579n;
const XXH_P5 = 2870177450012600261n;

const xxhMul = (a: bigint, b: bigint): bigint => (a * b) & XXH_MASK;
const xxhAdd = (a: bigint, b: bigint): bigint => (a + b) & XXH_MASK;
const xxhRotl = (v: bigint, r: bigint): bigint =>
  ((v << r) | (v >> (64n - r))) & XXH_MASK;

const xxhRound = (acc: bigint, input: bigint): bigint =>
  xxhMul(xxhRotl(xxhAdd(acc, xxhMul(input, XXH_P2)), 31n), XXH_P1);

const xxhMergeRound = (acc: bigint, val: bigint): bigint =>
  xxhAdd(xxhMul(acc ^ xxhRound(0n, val), XXH_P1), XXH_P4);

function xxhAvalanche(h: bigint): bigint {
  h ^= h >> 33n;
  h = xxhMul(h, XXH_P2);
  h ^= h >> 29n;
  h = xxhMul(h, XXH_P3);
  h ^= h >> 32n;
  return h;
}

/** xxHash64 of a UTF-8 string, seed 0 — the hash spheromak routes controllers and
 * topics by. */
export function controllerHash(input: string): bigint {
  const data = new TextEncoder().encode(input);
  const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
  const len = BigInt(data.length);
  let i = 0;
  let h: bigint;

  if (data.length >= 32) {
    let v1 = xxhAdd(XXH_P1, XXH_P2);
    let v2 = XXH_P2;
    let v3 = 0n;
    let v4 = (0n - XXH_P1) & XXH_MASK;
    for (; i <= data.length - 32; i += 32) {
      v1 = xxhRound(v1, view.getBigUint64(i, true));
      v2 = xxhRound(v2, view.getBigUint64(i + 8, true));
      v3 = xxhRound(v3, view.getBigUint64(i + 16, true));
      v4 = xxhRound(v4, view.getBigUint64(i + 24, true));
    }
    h = xxhAdd(
      xxhAdd(xxhRotl(v1, 1n), xxhRotl(v2, 7n)),
      xxhAdd(xxhRotl(v3, 12n), xxhRotl(v4, 18n)),
    );
    h = xxhMergeRound(h, v1);
    h = xxhMergeRound(h, v2);
    h = xxhMergeRound(h, v3);
    h = xxhMergeRound(h, v4);
  } else {
    h = xxhAdd(XXH_P5, 0n);
  }

  h = xxhAdd(h, len);

  for (; i + 8 <= data.length; i += 8) {
    h = xxhAdd(xxhMul(xxhRotl(h ^ xxhRound(0n, view.getBigUint64(i, true)), 27n), XXH_P1), XXH_P4);
  }
  if (i + 4 <= data.length) {
    h = xxhAdd(xxhMul(xxhRotl(h ^ xxhMul(BigInt(view.getUint32(i, true)), XXH_P1), 23n), XXH_P2), XXH_P3);
    i += 4;
  }
  for (; i < data.length; i += 1) {
    h = xxhMul(xxhRotl(h ^ xxhMul(BigInt(data[i] ?? 0), XXH_P5), 11n), XXH_P1);
  }

  return xxhAvalanche(h);
}

// =============================================================================
// /catalog Controller
// =============================================================================

class CatalogController {
  // Declared and assigned rather than a constructor parameter property,
  // which is TS-only syntax rejected under `erasableSyntaxOnly`.
  private client: SonolumeClient;

  constructor(client: SonolumeClient) {
    this.client = client;
  }

  /** Call /catalog/options */
  async options(request: void): Promise<SelectionOptions> {
    return this.client.sendRequest<SelectionOptions>(
      15541533792460894560n,
      16398749137410613924n,
      request,
      true
    );
  }
}

// =============================================================================
// /game Controller
// =============================================================================

class GameController {
  // Declared and assigned rather than a constructor parameter property,
  // which is TS-only syntax rejected under `erasableSyntaxOnly`.
  private client: SonolumeClient;

  constructor(client: SonolumeClient) {
    this.client = client;
  }

  /** Call /game/advance */
  async advance(request: void): Promise<GameAck> {
    return this.client.sendRequest<GameAck>(
      15399368766099108373n,
      6346892037440737563n,
      request,
      true
    );
  }

  /** Call /game/allow_steal */
  async allow_steal(request: void): Promise<GameAck> {
    return this.client.sendRequest<GameAck>(
      15399368766099108373n,
      1758730698366865368n,
      request,
      true
    );
  }

  /** Call /game/challenge */
  async challenge(request: ChallengeRequest): Promise<GameAck> {
    return this.client.sendRequest<GameAck>(
      15399368766099108373n,
      3625875736573881614n,
      request,
      true
    );
  }

  /** Call /game/deal */
  async deal(request: void): Promise<GameAck> {
    return this.client.sendRequest<GameAck>(
      15399368766099108373n,
      6259891458948905508n,
      request,
      true
    );
  }

  /** Call /game/defend_steal */
  async defend_steal(request: void): Promise<GameAck> {
    return this.client.sendRequest<GameAck>(
      15399368766099108373n,
      14281902123799171969n,
      request,
      true
    );
  }

  /** Call /game/flag */
  async flag(request: FlagRequest): Promise<GameAck> {
    return this.client.sendRequest<GameAck>(
      15399368766099108373n,
      11703139407706004208n,
      request,
      true
    );
  }

  /** Call /game/guess */
  async guess(request: GuessRequest): Promise<GameAck> {
    return this.client.sendRequest<GameAck>(
      15399368766099108373n,
      16881197374729774982n,
      request,
      true
    );
  }

  /** Call /game/lock_guess */
  async lock_guess(request: void): Promise<GameAck> {
    return this.client.sendRequest<GameAck>(
      15399368766099108373n,
      4797856787724101762n,
      request,
      true
    );
  }

  /** Call /game/pass_challenge */
  async pass_challenge(request: void): Promise<GameAck> {
    return this.client.sendRequest<GameAck>(
      15399368766099108373n,
      7919314929092345124n,
      request,
      true
    );
  }

  /** Call /game/place */
  async place(request: PlaceRequest): Promise<GameAck> {
    return this.client.sendRequest<GameAck>(
      15399368766099108373n,
      4332797192609382377n,
      request,
      true
    );
  }

  /** Call /game/purchase */
  async purchase(request: PurchaseRequest): Promise<GameAck> {
    return this.client.sendRequest<GameAck>(
      15399368766099108373n,
      10271315167667621807n,
      request,
      true
    );
  }

  /** Call /game/rate */
  async rate(request: RateRequest): Promise<RateAck> {
    return this.client.sendRequest<RateAck>(
      15399368766099108373n,
      5713640016798996577n,
      request,
      true
    );
  }

  /** Call /game/revote */
  async revote(request: void): Promise<GameAck> {
    return this.client.sendRequest<GameAck>(
      15399368766099108373n,
      2267141650882529028n,
      request,
      true
    );
  }

  /** Call /game/stall_vote */
  async stall_vote(request: StallVoteRequest): Promise<GameAck> {
    return this.client.sendRequest<GameAck>(
      15399368766099108373n,
      1735862318748737249n,
      request,
      true
    );
  }

  /** Call /game/start */
  async start(request: void): Promise<GameAck> {
    return this.client.sendRequest<GameAck>(
      15399368766099108373n,
      7558584164460087657n,
      request,
      true
    );
  }

  /** Call /game/steal */
  async steal(request: StealRequest): Promise<GameAck> {
    return this.client.sendRequest<GameAck>(
      15399368766099108373n,
      17640377907005019792n,
      request,
      true
    );
  }

  /** Call /game/watch */
  async watch(request: void): Promise<Watching> {
    return this.client.sendRequest<Watching>(
      15399368766099108373n,
      7984239503302635647n,
      request,
      true
    );
  }
}

// =============================================================================
// /party Controller
// =============================================================================

class PartyController {
  // Declared and assigned rather than a constructor parameter property,
  // which is TS-only syntax rejected under `erasableSyntaxOnly`.
  private client: SonolumeClient;

  constructor(client: SonolumeClient) {
    this.client = client;
  }

  /** Call /party/add_playlist */
  async add_playlist(request: AddPlaylistRequest): Promise<Ack> {
    return this.client.sendRequest<Ack>(
      9913697182705374820n,
      2427608554715734865n,
      request,
      true
    );
  }

  /** Call /party/configure */
  async configure(request: ConfigureRequest): Promise<Ack> {
    return this.client.sendRequest<Ack>(
      9913697182705374820n,
      13859833001518376408n,
      request,
      true
    );
  }

  /** Call /party/contribute_library */
  async contribute_library(request: ContributeLibraryRequest): Promise<Ack> {
    return this.client.sendRequest<Ack>(
      9913697182705374820n,
      12043400124676877397n,
      request,
      true
    );
  }

  /** Call /party/create_team */
  async create_team(request: CreateTeamRequest): Promise<Ack> {
    return this.client.sendRequest<Ack>(
      9913697182705374820n,
      2196080488337776034n,
      request,
      true
    );
  }

  /** Call /party/here */
  async here(request: void): Promise<Ack> {
    return this.client.sendRequest<Ack>(
      9913697182705374820n,
      5636037159777753840n,
      request,
      true
    );
  }

  /** Call /party/join_team */
  async join_team(request: JoinTeamRequest): Promise<Ack> {
    return this.client.sendRequest<Ack>(
      9913697182705374820n,
      3862943006427980303n,
      request,
      true
    );
  }

  /** Call /party/leave */
  async leave(request: void): Promise<Ack> {
    return this.client.sendRequest<Ack>(
      9913697182705374820n,
      8708114424555202739n,
      request,
      true
    );
  }

  /** Call /party/ready */
  async ready(request: ReadyRequest): Promise<Ack> {
    return this.client.sendRequest<Ack>(
      9913697182705374820n,
      17171380516818028527n,
      request,
      true
    );
  }

  /** Call /party/refresh_sources */
  async refresh_sources(request: ReadyRequest): Promise<Ack> {
    return this.client.sendRequest<Ack>(
      9913697182705374820n,
      780479971393591930n,
      request,
      true
    );
  }

  /** Call /party/remove_source */
  async remove_source(request: RemoveSourceRequest): Promise<Ack> {
    return this.client.sendRequest<Ack>(
      9913697182705374820n,
      18169644219949863137n,
      request,
      true
    );
  }

  /** Call /party/watch */
  async watch(request: void): Promise<Lobby> {
    return this.client.sendRequest<Lobby>(
      9913697182705374820n,
      7984239503302635647n,
      request,
      true
    );
  }
}

// =============================================================================
// Authentication helpers (binary wire codecs + Ed25519)
// =============================================================================

/** Whether a rejected auth frame was the server asking for a second factor.
 *
 * Matched on the numeric protocol CODE, never on the message — auth failure
 * messages are deliberately generic and non-enumerable. */
function isMfaRequired(error: unknown): boolean {
  return error instanceof SpheromakApiError && Number(error.code) === MFA_REQUIRED_CODE;
}

/** A decoded Ed25519 auth challenge. */
interface ParsedAuthChallenge {
  nonce: Uint8Array;
  timestamp: bigint;
  serverSignature: Uint8Array;
  serverPublicKey: Uint8Array;
}

function concatBytes(...parts: Uint8Array[]): Uint8Array {
  let total = 0;
  for (const p of parts) total += p.length;
  const out = new Uint8Array(total);
  let offset = 0;
  for (const p of parts) { out.set(p, offset); offset += p.length; }
  return out;
}

function bytesEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= (a[i] ?? 0) ^ (b[i] ?? 0);
  return diff === 0;
}

/** Encode a u64 as 8 big-endian bytes. */
function u64beBytes(value: bigint): Uint8Array {
  const out = new Uint8Array(8);
  let v = value;
  for (let i = 7; i >= 0; i--) { out[i] = Number(v & 0xffn); v >>= 8n; }
  return out;
}

/** Read a u64 from 8 big-endian bytes. */
function u64beRead(bytes: Uint8Array, offset: number): bigint {
  // Bounds-checked rather than defaulting the reads to zero. Both satisfy
  // `noUncheckedIndexedAccess`; only this one keeps a bad offset a loud failure
  // instead of a plausible-looking wrong number.
  if (offset < 0 || offset + 8 > bytes.length) {
    throw new Error('u64 read out of bounds');
  }
  let v = 0n;
  for (let i = 0; i < 8; i++) v = (v << 8n) | BigInt(bytes[offset + i] as number);
  return v;
}

/** Encode an AuthEnvelope: name_len(u8) ‖ name ‖ payload. */
function encodeAuthEnvelope(strategy: string, payload: Uint8Array): Uint8Array {
  const name = new TextEncoder().encode(strategy);
  if (name.length === 0 || name.length > 64) {
    throw new Error(`invalid strategy name length: ${name.length}`);
  }
  const out = new Uint8Array(1 + name.length + payload.length);
  out[0] = name.length;
  out.set(name, 1);
  out.set(payload, 1 + name.length);
  return out;
}

/** Decode an AuthEnvelope. */
function decodeAuthEnvelope(data: Uint8Array): { strategy: string; payload: Uint8Array } {
  if (data.length < 1) throw new Error('empty auth envelope');
  const nameLen = data[0] ?? 0;
  if (nameLen === 0 || nameLen > 64 || data.length < 1 + nameLen) {
    throw new Error('malformed auth envelope');
  }
  const strategy = new TextDecoder().decode(data.slice(1, 1 + nameLen));
  const payload = data.slice(1 + nameLen);
  return { strategy, payload };
}

/** Parse the binary Ed25519 challenge: nonce(32) ‖ ts(8) ‖ sig(64) ‖ pub(32). */
function parseAuthChallenge(payload: Uint8Array): ParsedAuthChallenge {
  if (payload.length < 32 + 8 + 64 + 32) {
    throw new Error('challenge payload too short');
  }
  return {
    nonce: payload.slice(0, 32),
    timestamp: u64beRead(payload, 32),
    serverSignature: payload.slice(40, 104),
    serverPublicKey: payload.slice(104, 136),
  };
}

/** Encode the binary AuthResponse:
 *  id_len(u16 BE) ‖ id ‖ client_pub(32) ‖ signature(64) ‖ is_registration(1). */
function encodeAuthResponse(
  clientId: string,
  clientPublicKey: Uint8Array,
  signature: Uint8Array,
  isRegistration: boolean,
): Uint8Array {
  const id = new TextEncoder().encode(clientId);
  const out = new Uint8Array(2 + id.length + 32 + 64 + 1);
  out[0] = (id.length >> 8) & 0xff;
  out[1] = id.length & 0xff;
  out.set(id, 2);
  out.set(clientPublicKey, 2 + id.length);
  out.set(signature, 2 + id.length + 32);
  out[2 + id.length + 32 + 64] = isRegistration ? 1 : 0;
  return out;
}

// =============================================================================
// Call identity binding (Phase 65, RT2)
// =============================================================================

/** Which end of a call produced a binding. */
export type CallRole = 'offerer' | 'answerer';

/** Normalize a DTLS-SRTP fingerprint the way the server does before signing.
 *
 * SDP fingerprint case is not guaranteed across implementations, so both ends uppercase
 * first — otherwise a signature would depend on a peer's formatting. */
export function normalizeFingerprint(fingerprint: string): string {
  const trimmed = fingerprint.trim();
  if (trimmed.length === 0) throw new Error('empty DTLS fingerprint');
  if (trimmed.length > 512) throw new Error('DTLS fingerprint too long');
  return trimmed.toUpperCase();
}

/** Build the exact bytes a peer signs to bind its media session to this call.
 *
 * Must match `spheromak::calls::binding_transcript` byte-for-byte:
 *
 *   "SPHEROMAK-CALL-BIND-v1\0"  domain separator, NUL-terminated
 *   call_id                      16 raw UUID bytes
 *   role                         1 byte, 0 = offerer, 1 = answerer
 *   u16-LE len || peer_client_id
 *   u16-LE len || fingerprint
 *
 * Signing the bare fingerprint would be broken: a fingerprint belongs to a long-lived DTLS
 * certificate reused across calls, so a signature over it alone is a credential replayable
 * into a call the signer never joined. The call id, the role and the intended peer are what
 * stop that. */
export function callBindingTranscript(
  callId: string,
  role: CallRole,
  peerClientId: string,
  fingerprint: string,
): Uint8Array {
  if (peerClientId.length === 0) throw new Error('empty peer client id');
  if (peerClientId.length > 256) throw new Error('peer client id too long');

  const domain = new TextEncoder().encode('SPHEROMAK-CALL-BIND-v1\0');
  const id = uuidToBytes(callId);
  const peer = new TextEncoder().encode(peerClientId);
  const fp = new TextEncoder().encode(normalizeFingerprint(fingerprint));

  const out = new Uint8Array(domain.length + 16 + 1 + 2 + peer.length + 2 + fp.length);
  let o = 0;
  out.set(domain, o); o += domain.length;
  out.set(id, o); o += 16;
  out[o] = role === 'offerer' ? 0 : 1; o += 1;
  out[o] = peer.length & 0xff; out[o + 1] = (peer.length >> 8) & 0xff; o += 2;
  out.set(peer, o); o += peer.length;
  out[o] = fp.length & 0xff; out[o + 1] = (fp.length >> 8) & 0xff; o += 2;
  out.set(fp, o);
  return out;
}

/** Sign a call binding with this peer's spheromak Ed25519 identity key (the same 32-byte
 * seed used for authentication). Returns the 64-byte signature. */
export async function signCallBinding(
  privateKey: Uint8Array,
  callId: string,
  role: CallRole,
  peerClientId: string,
  fingerprint: string,
): Promise<Uint8Array> {
  const ed = await loadEd25519();
  const seed = privateKey.length === 32 ? privateKey : privateKey.slice(0, 32);
  const transcript = callBindingTranscript(callId, role, peerClientId, fingerprint);
  return await ed.signAsync(transcript, seed);
}

/** Verify a peer's call binding against the identity the server authenticated.
 *
 * `peerClientId` is who the binding was addressed to — so verifying an INBOUND binding
 * means passing your own client id. That is what makes a relayed binding fail. */
export async function verifyCallBinding(
  publicKey: Uint8Array,
  callId: string,
  role: CallRole,
  peerClientId: string,
  fingerprint: string,
  signature: Uint8Array,
): Promise<boolean> {
  const ed = await loadEd25519();
  const transcript = callBindingTranscript(callId, role, peerClientId, fingerprint);
  try {
    return await ed.verifyAsync(signature, transcript, publicKey);
  } catch {
    return false;
  }
}

/** Decode a hyphenated UUID string into its 16 raw bytes. */
function uuidToBytes(uuid: string): Uint8Array {
  const hex = uuid.replace(/-/g, '');
  if (!/^[0-9a-fA-F]{32}$/.test(hex)) throw new Error(`invalid call id: ${uuid}`);
  const out = new Uint8Array(16);
  for (let i = 0; i < 16; i++) out[i] = parseInt(hex.substr(i * 2, 2), 16);
  return out;
}

/** Load @noble/ed25519 with a WebCrypto SHA-512 (required by noble v2). */
async function loadEd25519(): Promise<any> {
  // @ts-ignore - optional peer dependency, resolved at runtime
  // @vite-ignore tells the BUNDLER the same thing @ts-ignore tells the type
  // checker. Without it vite's import analysis resolves this eagerly at
  // transform time and the dev server refuses to serve the page at all —
  // even for an app that authenticates another way and never calls this.
  // Rollup leaves the bare specifier alone, so production builds looked fine
  // and only `vite dev` broke: the two toolchains disagreed, and the one that
  // disagreed is the one consumers develop in.
  const ed = await import(/* @vite-ignore */ '@noble/ed25519');
  if (!ed.etc.sha512Async || (ed.etc as any).__spheromakHashSet !== true) {
    ed.etc.sha512Async = async (...msgs: Uint8Array[]) => {
      const digest = await crypto.subtle.digest('SHA-512', concatBytes(...msgs) as BufferSource);
      return new Uint8Array(digest);
    };
    (ed.etc as any).__spheromakHashSet = true;
  }
  return ed;
}

